/**
 * AgentDetailPage — operations cockpit for a single Digital Employee.
 *
 * One operator surface, grouped into headed areas: work center (workspace + conversation),
 * runs and performance, connections and integrations, then configuration and permissions.
 *
 * Route: /agents/:id
 * Access: agents_view required to open; mutating actions (edit, connections, checks) require agents_manage.
 */
import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { agentsApi, agentRunsApi, skillsApi, scheduledChecksApi, agentConnectionsApi, companiesApi } from "../services/api";
import EmployeeAvatar, { STATUS_META, drawnStatus } from "../components/EmployeeAvatar";
import { degradedAnalysisLabel } from "../components/degradedAnalysisVocabulary";
import EmployeeWorkspace, { connectionReadinessQueryKey } from "../components/EmployeeWorkspace";
import { useAgentPermissions } from "../context/AgentPermissionsContext";
import { useNotification } from "../context/NotificationContext";
import { useAuth } from "../context/AuthContext";
import { isUnrestrictedAllowlist } from "../utils/toolAllowlist";
import { buildAgentUpdatePayload } from "../utils/agentPayload";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const AUTONOMY_LABELS = {
  approval_required: "אישור נדרש (מפוקח מלא)",
  supervised:        "מפוקח",
  semi_autonomous:   "חצי אוטונומי",
  autonomous:        "אוטונומי מלא",
};

const TIER_CONFIG = {
  tier_1: { badge: "T1", label: "קריאה בלבד",  cls: "bg-green-100 text-green-700" },
  tier_2: { badge: "T2", label: "תפעולי",       cls: "bg-amber-100 text-amber-700" },
  tier_3: { badge: "T3", label: "מוגבה",        cls: "bg-red-100 text-red-700" },
};

const STATUS_LABELS = {
  pending:                         "מאתחל",
  analyzing:                       "מנתח",
  awaiting_technician_review:      "ממתין לסקירה",
  awaiting_approval:               "ממתין לאישור",
  waiting_for_customer_connection: "ממתין לחיבור",
  connection_active:               "חיבור פעיל",
  waiting_for_customer_answer:     "ממתין לתשובה",
  diagnosing:                      "מאבחן",
  paused:                          "הופסק",
  resolved:                        "נפתרה",
  completed:                       "הושלם",
  approved:                        "מאושר",
  rejected:                        "נדחה",
  executing:                       "מבצע",
  failed:                          "נכשל",
};

const STATUS_COLOR = {
  // waiting on a human
  awaiting_technician_review: "bg-orange-100 text-orange-700",
  awaiting_approval:          "bg-orange-100 text-orange-700",
  // in-flight work
  pending:                    "bg-blue-100 text-blue-700",
  analyzing:                  "bg-blue-100 text-blue-700",
  diagnosing:                 "bg-blue-100 text-blue-700",
  executing:                  "bg-blue-100 text-blue-700",
  approved:                   "bg-teal-100 text-teal-700",
  connection_active:          "bg-teal-100 text-teal-700",
  // waiting on the customer
  waiting_for_customer_connection: "bg-amber-50 text-amber-700",
  waiting_for_customer_answer:     "bg-amber-50 text-amber-700",
  // terminal
  completed:                  "bg-green-100 text-green-700",
  resolved:                   "bg-green-100 text-green-700",
  failed:                     "bg-red-100 text-red-700",
  rejected:                   "bg-red-100 text-red-700",
  paused:                     "bg-gray-100 text-gray-600",
};

const CONNECTION_TYPE_LABELS = {
  openclaw:     "OpenClaw",
  teamviewer:   "TeamViewer",
  ms365:        "Microsoft 365",
  sharepoint:   "SharePoint",
  graph:        "Microsoft Graph",
  m365_graph:   "Microsoft Graph (M365)",
  eset_protect: "ESET Protect",
  acronis_backup: "Acronis Backup",
  custom:       "מותאם אישית",
};

const ABS_TIME_FMT = new Intl.DateTimeFormat("he-IL", { dateStyle: "short", timeStyle: "short" });

// Run timestamps may arrive as naive UTC (no Z/offset); parsing them as local time skews
// recency display by the UTC offset (same coercion rule as ActivityLogPage.timeAgo).
function parseUtcDate(iso) {
  if (!iso) return null;
  const utc = !iso.endsWith("Z") && !/[+-]\d{2}:?\d{2}$/.test(iso) ? `${iso}Z` : iso;
  const date = new Date(utc);
  return Number.isNaN(date.getTime()) ? null : date;
}

function fmtAbsTime(iso) {
  const date = parseUtcDate(iso);
  return date ? ABS_TIME_FMT.format(date) : "";
}

function fmtRelTime(iso) {
  const date = parseUtcDate(iso);
  if (!date) return "—";
  const minutes = Math.floor((Date.now() - date.getTime()) / 60_000);
  if (minutes < 1) return "לפני רגע";
  if (minutes < 60) return `לפני ${minutes} דקות`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `לפני ${hours} שעות`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `לפני ${days} ימים`;
  return ABS_TIME_FMT.format(date);
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Intl.DateTimeFormat("he-IL", { dateStyle: "short", timeStyle: "short" }).format(new Date(iso));
}

function StatusChip({ status }) {
  const color = STATUS_COLOR[status] || "bg-gray-100 text-gray-600";
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${color}`}>
      {STATUS_LABELS[status] || status}
    </span>
  );
}

// ─── EditableField ─────────────────────────────────────────────────────────────

function Field({ label, children }) {
  return (
    <div>
      <p className="text-xs text-gray-500 mb-0.5">{label}</p>
      <div className="text-sm text-gray-800">{children}</div>
    </div>
  );
}

// ─── CockpitSection ───────────────────────────────────────────────────────────

// The page is one cockpit: every area opens with an operator-facing heading and a one-line
// description of what the area answers, so the sections read as parts of a single screen.
function CockpitSection({ title, description, children }) {
  return (
    <section aria-label={title} className="space-y-4">
      <div className="flex items-start gap-3">
        <span aria-hidden="true" className="mt-1.5 h-5 w-1 shrink-0 rounded-full bg-brand-600" />
        <div>
          <h2 className="text-lg font-semibold text-gray-800">{title}</h2>
          {description && <p className="text-xs text-gray-500 mt-0.5">{description}</p>}
        </div>
      </div>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

// ─── ConfigSection ─────────────────────────────────────────────────────────────

function ConfigSection({ agent, onEdit, canManage }) {
  return (
    <div className="bg-white rounded-xl border shadow-sm p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-gray-700">הגדרות</h3>
        {canManage && (
          <button
            type="button"
            onClick={onEdit}
            className="text-xs text-brand-600 hover:underline"
          >
            ערוך
          </button>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="שם">{agent.name}</Field>
        <Field label="מודל">
          <span className="font-mono text-xs" dir="ltr">{agent.model}</span>
        </Field>
        <Field label="חברה">{agent.client_name || "ללא"}</Field>
        <Field label="רמת הרשאה">
          {(() => {
            const t = TIER_CONFIG[agent.permission_tier];
            if (!t) return agent.permission_tier || "tier_1";
            return <><span className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${t.cls}`}>{t.badge}</span> {t.label}</>;
          })()}
        </Field>
        <Field label="כלים מותרים">
          {isUnrestrictedAllowlist(agent.allowed_tools)
            ? <span className="text-gray-400 text-xs">כל הכלים ברמה זו</span>
            : (
              <div className="flex flex-wrap gap-1">
                {agent.allowed_tools.map((t) => (
                  <span key={t} className="px-1.5 py-0.5 bg-blue-50 text-blue-700 text-[10px] rounded border border-blue-200">{t}</span>
                ))}
              </div>
            )
          }
        </Field>
        <Field label="רמת אוטונומיה">
          {AUTONOMY_LABELS[agent.autonomy_level] || agent.autonomy_level}
        </Field>
        <Field label="נוצר">{fmtDate(agent.created_at)}</Field>
        <Field label="נוצר על ידי">{agent.created_by}</Field>
        {agent.description && (
          <div className="col-span-2">
            <Field label="תיאור">{agent.description}</Field>
          </div>
        )}
      </div>
      {agent.system_prompt && (
        <div>
          <p className="text-xs text-gray-500 mb-0.5">System prompt</p>
          <pre className="text-xs bg-gray-50 rounded-lg border p-3 whitespace-pre-wrap font-mono text-gray-700 max-h-40 overflow-y-auto" dir="ltr">
            {agent.system_prompt}
          </pre>
        </div>
      )}
    </div>
  );
}

// ─── ConnectionsSection ────────────────────────────────────────────────────────

// CONNECTION-HUB-001: fallback only, used the instant before /api/agents/connector-types has
// loaded (or if it fails) so the form never renders with an empty type list. Once loaded, the
// registry response (agent-connection-backed connector types only) replaces this entirely.
const FALLBACK_SYSTEM_TYPE_OPTIONS = [
  { value: "m365_graph", label: "Microsoft Graph (M365)" },
  { value: "eset_protect", label: "ESET Protect" },
];

const CAPABILITY_LABELS = {
  device_security_status: "מצב הגנת מכשיר",
  threat_detections: "איתור איומים",
  directory_security_read: "קריאת אבטחת ספרייה",
  user_security_inspection: "בדיקת אבטחת משתמש",
  backup_status: "מצב גיבוי",
  backup_alerts: "התרעות גיבוי",
  protected_devices: "מכשירים מגובים",
  device_lookup: "איתור מכשיר",
  attended_session: "הפעלת תמיכה",
  endpoint_diagnostics: "אבחון נקודת קצה",
  log_collection: "איסוף לוגים",
};

// ESET-CAPABILITY-PROMOTION-001: learned-capability promotion status, as returned by
// GET /api/agents/{id}/connections/{id}/capabilities (capability_promotion.annotate_catalog_payload).
// Never computed client-side — the backend's static PROMOTION_MAP is the only source of truth.
const PROMOTION_STATUS_LABELS = {
  discovered: "נלמד בלבד",
  reviewed_read_only: "נסקר (קריאה)",
  promoted_tool: "מאושר לקריאה",
  proposed_write: "דורש אישור/סקירה",
  blocked: "חסום",
};
const PROMOTION_STATUS_BADGE_CLASS = {
  discovered: "bg-gray-100 text-gray-600 border-gray-200",
  reviewed_read_only: "bg-blue-50 text-blue-700 border-blue-200",
  promoted_tool: "bg-green-50 text-green-700 border-green-200",
  proposed_write: "bg-amber-50 text-amber-700 border-amber-200",
  blocked: "bg-red-50 text-red-700 border-red-200",
};

const EMPTY_CONN_FORM = {
  system_type: "m365_graph", display_name: "", tenant_id: "", client_id_app: "", secret_ref: "",
  // ACRONIS-CONNECTOR-001: only rendered/sent when the selected connector declares these fields
  // (currently only acronis_backup) — see ConnectorField-driven rendering below.
  external_tenant_id: "",
  partner_tenant_id: "",
};

const ESET_STATUS_LABELS = {
  DEVICE_FUNCTIONALITY_STATUS_OK: "תקין",
  DEVICE_FUNCTIONALITY_STATUS_ATTENTION_RECOMMENDED: "דורש תשומת לב",
  DEVICE_FUNCTIONALITY_STATUS_ATTENTION_REQUIRED: "דורש טיפול",
  DEVICE_FUNCTIONALITY_STATUS_UNSPECIFIED: "לא ידוע",
};

function formatEsetDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString("he-IL");
}

function esetDeviceProblemText(device) {
  const count = device?.functionality_problem_count ?? 0;
  const status = ESET_STATUS_LABELS[device?.functionality_status] || device?.functionality_status || "לא ידוע";
  const detections = device?.recent_detections || [];
  const detectionText = detections
    .map((d) => d.name || d.circumstances || d.object_name)
    .filter(Boolean)
    .slice(0, 3)
    .join(" · ");
  if (detectionText) return `${status} · ${count} בעיות · ${detectionText}`;
  return `${status} · ${count} בעיות`;
}

function ESETAdvisoryPanel({ agentId, connectionId }) {
  const cacheKey = `prodigy:eset-advisory:${agentId}:${connectionId}`;
  const [customers, setCustomers] = useState([]);
  const [companyRoots, setCompanyRoots] = useState([]);
  const [scopeSource, setScopeSource] = useState("");
  const [selectedCustomerUuid, setSelectedCustomerUuid] = useState("");
  const [selectedGroupUuid, setSelectedGroupUuid] = useState("");
  const [question, setQuestion] = useState("מה הבעיות כרגע אצל הלקוח?");
  const [result, setResult] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [diagnostic, setDiagnostic] = useState(null);
  const [problemOnly, setProblemOnly] = useState(false);

  const selectedCustomer = customers.find((customer) => customer.customer_scope_uuid === selectedCustomerUuid);
  const groups = selectedCustomer?.groups || [];

  const customersMutation = useMutation({
    mutationFn: () => agentConnectionsApi.advisoryCustomers(agentId, connectionId),
    onSuccess: (data) => {
      const items = data?.customers || [];
      setCustomers(items);
      setCompanyRoots(data?.company_roots || []);
      setScopeSource(data?.customer_scope_source || "");
      setLoadError("");
      setResult(null);

      let nextCustomerUuid = selectedCustomerUuid;
      let nextGroupUuid = selectedGroupUuid;
      const existingCustomer = items.find((item) => item.customer_scope_uuid === nextCustomerUuid);
      if (!existingCustomer) {
        if (items.length === 1) {
          nextCustomerUuid = items[0].customer_scope_uuid;
          const rootGroup = (items[0].groups || []).find((group) => group.is_scope_root) || items[0].groups?.[0];
          nextGroupUuid = rootGroup?.group_uuid || "";
        } else {
          nextCustomerUuid = "";
          nextGroupUuid = "";
        }
      } else if (!(existingCustomer.groups || []).some((group) => group.group_uuid === nextGroupUuid)) {
        const rootGroup = (existingCustomer.groups || []).find((group) => group.is_scope_root) || existingCustomer.groups?.[0];
        nextGroupUuid = rootGroup?.group_uuid || "";
      }

      setSelectedCustomerUuid(nextCustomerUuid);
      setSelectedGroupUuid(nextGroupUuid);
      try {
        window.localStorage.setItem(cacheKey, JSON.stringify({
          saved_at: Date.now(),
          data,
          selected_customer_uuid: nextCustomerUuid,
          selected_group_uuid: nextGroupUuid,
        }));
      } catch (_) {
        // Browser storage is an optional UX cache only.
      }
    },
    onError: (err) => {
      setLoadError(err?.response?.data?.detail || "לא ניתן לטעון לקוחות ESET");
    },
  });

  useEffect(() => {
    let restored = false;
    try {
      const raw = window.localStorage.getItem(cacheKey);
      if (raw) {
        const cached = JSON.parse(raw);
        const data = cached?.data;
        const items = data?.customers || [];
        if (Array.isArray(items) && items.length > 0) {
          restored = true;
          setCustomers(items);
          setCompanyRoots(data?.company_roots || []);
          setScopeSource(data?.customer_scope_source || "");
          setSelectedCustomerUuid(cached?.selected_customer_uuid || "");
          setSelectedGroupUuid(cached?.selected_group_uuid || "");
          setLoadError("");
        }
      }
    } catch (_) {
      // Corrupt/unavailable browser storage should never block live ESET loading.
    }
    if (!restored) customersMutation.mutate();
    // Load once per agent+connection. Manual refresh remains available.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey]);

  const diagnosticMutation = useMutation({
    mutationFn: () => agentConnectionsApi.advisoryGroupTreeDiagnostic(agentId, connectionId),
    onSuccess: (data) => setDiagnostic(data),
    onError: (err) => setDiagnostic({
      error: err?.response?.data?.detail || "לא ניתן לקרוא את מבנה הקבוצות",
    }),
  });

  const queryMutation = useMutation({
    mutationFn: () => agentConnectionsApi.advisoryQuery(agentId, connectionId, {
      customer_scope_uuid: selectedCustomerUuid,
      group_uuid: selectedGroupUuid,
      question: question.trim(),
    }),
    onSuccess: (data) => setResult(data),
    onError: (err) => setResult({
      error: err?.response?.data?.detail || "לא ניתן לקבל כרגע תשובה מ-ESET",
    }),
  });

  const persistSelection = (customerUuid, groupUuid) => {
    try {
      const raw = window.localStorage.getItem(cacheKey);
      const cached = raw ? JSON.parse(raw) : {};
      window.localStorage.setItem(cacheKey, JSON.stringify({
        ...cached,
        saved_at: cached?.saved_at || Date.now(),
        selected_customer_uuid: customerUuid,
        selected_group_uuid: groupUuid,
      }));
    } catch (_) {
      // Selection persistence is best-effort only.
    }
  };

  const onCustomerChange = (customerUuid) => {
    setSelectedCustomerUuid(customerUuid);
    setResult(null);
    const customer = customers.find((item) => item.customer_scope_uuid === customerUuid);
    const rootGroup = (customer?.groups || []).find((group) => group.is_scope_root) || customer?.groups?.[0];
    const groupUuid = rootGroup?.group_uuid || "";
    setSelectedGroupUuid(groupUuid);
    persistSelection(customerUuid, groupUuid);
  };

  const onGroupChange = (groupUuid) => {
    setSelectedGroupUuid(groupUuid);
    setResult(null);
    persistSelection(selectedCustomerUuid, groupUuid);
  };

  const submit = (e) => {
    e.preventDefault();
    if (!selectedCustomerUuid || !selectedGroupUuid || !question.trim()) return;
    setResult(null);
    queryMutation.mutate();
  };

  return (
    <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-4 space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <p className="text-sm font-semibold text-emerald-900">עבודה מול ESET — לקוח וקבוצה</p>
          <p className="text-[11px] text-emerald-700">קריאה בלבד · ללא קריאות שירות · ללא AgentRun · ללא פעולות</p>
        </div>
        <button
          type="button"
          onClick={() => customersMutation.mutate()}
          disabled={customersMutation.isPending}
          className="text-xs px-2 py-1 rounded border border-emerald-300 text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
        >
          {customersMutation.isPending ? "מרענן…" : "רענן מ-ESET"}
        </button>
      </div>

      {loadError && <p className="text-xs text-red-600">{loadError}</p>}

      {customers.length > 0 && (
        <form onSubmit={submit} className="space-y-2">
          <div className="rounded border border-emerald-200 bg-white p-2 text-[11px] text-gray-600 space-y-1">
            <div className="flex flex-wrap gap-3">
              <span>
                מקור לקוחות: {scopeSource === "eset_static_groups_under_all"
                  ? "ESET Static Groups תחת All"
                  : scopeSource === "prodigy_company_name_mapping"
                    ? "חברות Prodigy ↔ קבוצות ESET תחת All"
                    : scopeSource === "msp_company_tree"
                    ? "ESET MSP → חברות לקוח"
                    : scopeSource === "company_direct_child_group"
                      ? "קבוצות ישירות תחת Prodigy"
                      : scopeSource === "site"
                        ? "ESET Sites"
                        : "ESET Company"}
              </span>
              <span>חברות ראשיות: {companyRoots.length}</span>
              <span>לקוחות שנמצאו: {customers.length}</span>
              {scopeSource === "eset_static_groups_under_all" && (
                <span>Prodigy mapping: אופציונלי</span>
              )}
              {scopeSource === "prodigy_company_name_mapping" && (
                <span>מיפוי: שם חברה מלא ומנורמל</span>
              )}
            </div>
            <p className="text-amber-700">
              רשימת הלקוחות נטענת תחילה ללא סריקת כל המכשירים. נתוני מכשירים/Subscription נקראים רק עבור לקוח שנבחר ונשאל עליו.
            </p>
          </div>

          <label className="block text-xs text-gray-600">
            שלב 1 — לקוח / אתר ב-ESET
            <select
              value={selectedCustomerUuid}
              onChange={(e) => onCustomerChange(e.target.value)}
              className="mt-1 w-full border rounded px-2 py-1.5 text-sm bg-white"
            >
              <option value="">בחר לקוח…</option>
              {customers.map((customer) => (
                <option key={customer.customer_scope_uuid} value={customer.customer_scope_uuid}>
                  {customer.display_name} · {customer.license_observation_status === "not_requested"
                    ? "נתונים ייטענו בבחירה"
                    : `${customer.device_count ?? "?"} מחשבים · ${customer.license_observation_status === "unavailable" ? "רישוי לא זמין" : `${(customer.subscriptions_observed || []).length} רישיונות נצפים`}`}
                </option>
              ))}
            </select>
          </label>

          {selectedCustomer && (
            <div className="rounded border bg-white p-2 text-[11px] text-gray-600 space-y-1">
              <div className="flex flex-wrap gap-3">
                {selectedCustomer.license_observation_status === "not_requested" ? (
                  <span className="text-emerald-700">נתוני מכשירים ורישוי ייקראו רק כשתשאל על הלקוח</span>
                ) : (
                  <>
                    <span>מחשבים: {selectedCustomer.device_count ?? "לא זמין"}</span>
                    {selectedCustomer.license_observation_status === "unavailable" ? (
                      <span className="text-amber-700">רישוי נצפה: לא זמין כרגע</span>
                    ) : (
                      <>
                        <span>Subscription נצפים: {(selectedCustomer.subscriptions_observed || []).length}</span>
                        <span>מוצרים: {(selectedCustomer.products_observed || []).length}</span>
                      </>
                    )}
                  </>
                )}
              </div>
              {selectedCustomer.license_observation_status === "unavailable" && (
                <p className="text-amber-700">הלקוח והקבוצות נטענו; רק קריאת נתוני הרישוי/מכשירים החלקית נכשלה.</p>
              )}
              {!!selectedCustomer.products_observed?.length && (
                <p>מוצרים: {selectedCustomer.products_observed.join(", ")}</p>
              )}
            </div>
          )}

          <label className="block text-xs text-gray-600">
            שלב 2 — קבוצה אצל הלקוח
            <select
              value={selectedGroupUuid}
              onChange={(e) => onGroupChange(e.target.value)}
              disabled={!selectedCustomerUuid}
              className="mt-1 w-full border rounded px-2 py-1.5 text-sm bg-white disabled:bg-gray-100"
            >
              <option value="">בחר קבוצה…</option>
              {groups.map((group) => (
                <option key={group.group_uuid} value={group.group_uuid}>
                  {group.is_scope_root ? "כל הלקוח — " : ""}{group.display_name}
                </option>
              ))}
            </select>
          </label>

          <div className="flex gap-2">
            <input
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              maxLength={500}
              placeholder="מה הבעיות כרגע אצל הלקוח?"
              className="flex-1 border rounded px-2 py-1.5 text-sm bg-white"
            />
            <button
              type="submit"
              disabled={!selectedCustomerUuid || !selectedGroupUuid || !question.trim() || queryMutation.isPending}
              className="bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded text-xs font-medium disabled:opacity-50"
            >
              {queryMutation.isPending ? "בודק…" : "שאל"}
            </button>
          </div>
        </form>
      )}

      {customersMutation.isSuccess && customers.length === 0 && !loadError && (
        <div className="space-y-2">
          <p className="text-xs text-amber-700">
            לא נמצאו Static Groups ישירים תחת All שניתן להציג כלקוחות ESET.
          </p>
          <button
            type="button"
            onClick={() => diagnosticMutation.mutate()}
            disabled={diagnosticMutation.isPending}
            className="text-xs px-2 py-1 rounded border border-amber-300 text-amber-700 hover:bg-amber-50 disabled:opacity-50"
          >
            {diagnosticMutation.isPending ? "קורא מבנה…" : "אבחן מבנה קבוצות"}
          </button>
        </div>
      )}

      {diagnostic && (
        <div className="rounded border border-amber-200 bg-amber-50 p-2 text-[11px] text-gray-700 space-y-2">
          {diagnostic.error ? (
            <p className="text-red-600">{diagnostic.error}</p>
          ) : (
            <>
              <p className="font-semibold">מבנה ESET שנקרא מה-API ({diagnostic.group_count ?? 0} קבוצות)</p>
              <div className="max-h-64 overflow-auto space-y-1 font-mono text-[10px]" dir="ltr">
                {(diagnostic.groups || []).map((group) => (
                  <div key={group.uuid} className="border-b border-amber-100 pb-1">
                    <div>{group.display_name}</div>
                    <div>type={group.linked_entity_type || "-"}</div>
                    <div>static={group.is_security_group === true ? "true" : group.is_security_group === false ? "false" : "-"}</div>
                    <div>uuid={group.uuid}</div>
                    <div>parent={group.parent_group_uuid || "-"}</div>
                  </div>
                ))}
              </div>
              <p className="text-amber-700">האבחון מציג metadata של קבוצות בלבד — ללא מכשירים, סודות או פעולות.</p>
            </>
          )}
        </div>
      )}

      {result && (
        <div className="rounded border bg-white p-3 text-xs space-y-2">
          {result.error ? (
            <p className="text-red-600">{result.error}</p>
          ) : (
            <>
              <div className="flex items-center justify-between gap-2">
                <p className="font-semibold text-gray-800">
                  {result.customer?.display_name || "ESET"}
                  {result.group?.display_name ? ` · ${result.group.display_name}` : ""}
                </p>
                <span className="text-green-700">Read-only</span>
              </div>
              <p className="text-sm text-gray-800 leading-6">{result.answer}</p>
              {!!result.findings?.length && (
                <ul className="list-disc pr-5 space-y-1 text-gray-600">
                  {result.findings.map((finding, idx) => <li key={idx}>{finding}</li>)}
                </ul>
              )}

              {!!result.devices?.length && (
                <div className="border rounded-lg overflow-hidden">
                  <div className="px-3 py-2 bg-gray-50 border-b flex items-center justify-between gap-3 flex-wrap">
                    <div>
                      <p className="font-semibold text-gray-700">מכשירים ב-ESET</p>
                      <p className="text-[11px] text-gray-500">
                        {result.devices.length} שורות שנקראו · Last connected מבוסס על lastSyncTime של ESET
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setProblemOnly(false)}
                        className={`px-2 py-1 rounded border text-[11px] ${!problemOnly ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-gray-600 border-gray-300"}`}
                      >
                        כל המחשבים
                      </button>
                      <button
                        type="button"
                        onClick={() => setProblemOnly(true)}
                        className={`px-2 py-1 rounded border text-[11px] ${problemOnly ? "bg-red-600 text-white border-red-600" : "bg-white text-gray-600 border-gray-300"}`}
                      >
                        רק בעייתיים
                      </button>
                      {result.truncated && (
                        <span className="text-[10px] px-2 py-0.5 rounded bg-amber-100 text-amber-700">
                          הרשימה חלקית
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="max-h-[36rem] overflow-y-auto overflow-x-hidden">
                    <table className="w-full table-fixed text-[10px] border-collapse">
                      <colgroup>
                        <col style={{ width: "13%" }} />
                        <col style={{ width: "10%" }} />
                        <col style={{ width: "23%" }} />
                        <col style={{ width: "18%" }} />
                        <col style={{ width: "12%" }} />
                        <col style={{ width: "18%" }} />
                        <col style={{ width: "6%" }} />
                      </colgroup>
                      <thead className="sticky top-0 bg-gray-100 text-gray-600 z-10">
                        <tr>
                          <th className="px-1.5 py-2 text-right border-b">שם מחשב</th>
                          <th className="px-1.5 py-2 text-right border-b">סטטוס</th>
                          <th className="px-1.5 py-2 text-right border-b">הבעיה לפי ESET</th>
                          <th className="px-1.5 py-2 text-right border-b">פעולה נדרשת</th>
                          <th className="px-1.5 py-2 text-right border-b">Last connected</th>
                          <th className="px-1.5 py-2 text-right border-b">פרטי מחשב</th>
                          <th className="px-1.5 py-2 text-right border-b">ביצוע</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(result.devices || [])
                          .filter((device) => !problemOnly
                            || device.functionality_status !== "DEVICE_FUNCTIONALITY_STATUS_OK"
                            || (device.functionality_problem_count || 0) > 0)
                          .map((device) => {
                            const detections = device.recent_detections || [];
                            const products = (device.active_products || []).map((p) => p.name).filter(Boolean);
                            const components = (device.deployed_components || [])
                              .map((component) => component.version_name ? `${component.name} ${component.version_name}` : component.name)
                              .filter(Boolean);
                            const detectionResponses = (detections || [])
                              .flatMap((d) => (d.responses || [])
                                .map((response) => response.description || response.display_name)
                                .filter(Boolean))
                              .slice(0, 4);
                            const problemEvidence = [
                              ...(detections || []).map((d) => d.circumstances || d.name || d.object_name).filter(Boolean).slice(0, 3),
                              ...detectionResponses,
                              device.detail_enrichment_status === "ok" ? "פרטי מכשיר מלאים נשלפו" : null,
                            ].filter(Boolean);
                            return (
                              <tr key={device.uuid} className="border-b last:border-0 align-top hover:bg-gray-50">
                                <td className="px-1.5 py-2 break-words">
                                  <div className="font-medium text-gray-800" dir="ltr">{device.display_name || "—"}</div>
                                  {device.original_display_name && device.original_display_name !== device.display_name && (
                                    <div className="text-[9px] text-gray-400 break-all" dir="ltr">{device.original_display_name}</div>
                                  )}
                                </td>
                                <td className="px-1.5 py-2 break-words">
                                  <div className={device.functionality_status === "DEVICE_FUNCTIONALITY_STATUS_OK" ? "text-green-700" : "text-red-700"}>
                                    {ESET_STATUS_LABELS[device.functionality_status] || device.functionality_status || "לא ידוע"}
                                    {device.functionality_problem_count ? ` · ${device.functionality_problem_count}` : ""}
                                  </div>
                                </td>
                                <td className="px-1.5 py-2 break-words leading-4">
                                  <div className="text-gray-800">
                                    {device.eset_problem_detail || device.derived_protection_reason || esetDeviceProblemText(device)}
                                  </div>
                                  {device.derived_protection_reason && (
                                    <div className="mt-1 text-[9px] font-medium text-amber-700" dir="ltr">
                                      {device.derived_protection_reason}
                                    </div>
                                  )}
                                  {!!problemEvidence.length && (
                                    <div className="mt-1 text-[9px] text-gray-500">
                                      {problemEvidence.join(" · ")}
                                    </div>
                                  )}
                                  {device.problem_evidence_source && (
                                    <div className="mt-1 text-[9px]">
                                      {device.problem_evidence_source === "incident_detection_detail" ? (
                                        <span className="text-emerald-700">מקור: Incident Management · Detection detail</span>
                                      ) : device.problem_evidence_coverage === "functionality_summary_only" ? (
                                        <span className="text-amber-700">מקור: Device Management · סטטוס/מונה בלבד; טקסט Alert מפורט לא נחשף ב-API הציבורי</span>
                                      ) : (
                                        <span className="text-gray-400">מקור: Device Management</span>
                                      )}
                                    </div>
                                  )}
                                </td>
                                <td className="px-1.5 py-2 break-words leading-4">
                                  <div className={device.required_action === "לא נדרשת פעולה" ? "text-green-700" : "text-amber-800"}>
                                    {device.required_action || "נדרשת בדיקה ידנית; לא התקבל פירוט פעולה נוסף מה-API"}
                                  </div>
                                </td>
                                <td className="px-1.5 py-2 break-words" dir="ltr">{formatEsetDate(device.last_sync_utc)}</td>
                                <td className="px-1.5 py-2 break-words leading-4 text-gray-600">
                                  <div><b>OS:</b> {device.operating_system || "—"}{device.os_bitness ? ` ${device.os_bitness}-bit` : ""}</div>
                                  <div><b>IP:</b> <span dir="ltr">{device.primary_local_ip || device.public_ip || "—"}</span></div>
                                  <div><b>ESET:</b> {device.security_application || (products.length ? products.join(", ") : "—")}</div>
                                  {device.security_application_source && device.security_application_source !== "none" && (
                                    <div className="text-[9px] text-gray-400"><b>מקור מוצר:</b> {device.security_application_source}</div>
                                  )}
                                  {components.length > 0 && <div><b>רכיבים:</b> {components.slice(0, 3).join(", ")}</div>}
                                  {device.management_domain && <div><b>Domain:</b> {device.management_domain}</div>}
                                  {(device.tags || []).length > 0 && <div><b>Tags:</b> {device.tags.join(", ")}</div>}
                                  <div className={device.logged_user ? "text-gray-600" : "text-gray-400"}>
                                    <b>משתמש:</b> {device.logged_user || "לא הוחזר בנתוני המכשיר"}
                                  </div>
                                  {(device.vulnerability_count != null || device.alert_count != null || device.detection_count != null || device.incident_count != null) && (
                                    <div className="mt-1 text-[9px] text-gray-500">
                                      <b>ראיות:</b>{" "}
                                      {[
                                        device.vulnerability_count != null ? `Vuln ${device.vulnerability_count}` : null,
                                        device.alert_count != null ? `Alerts ${device.alert_count}` : null,
                                        device.detection_count != null ? `Detections ${device.detection_count}` : null,
                                        device.incident_count != null ? `Incidents ${device.incident_count}` : null,
                                        device.open_incident_count != null ? `Open ${device.open_incident_count}` : null,
                                      ].filter(Boolean).join(" · ")}
                                    </div>
                                  )}
                                  {(device.last_detection_user || device.last_detection_at || device.last_detection_severity) && (
                                    <div className="mt-1 text-[9px] text-gray-500">
                                      <b>Detection אחרון:</b>{" "}
                                      {[
                                        device.last_detection_severity,
                                        device.last_detection_user,
                                        device.last_detection_at ? formatEsetDate(device.last_detection_at) : null,
                                      ].filter(Boolean).join(" · ")}
                                    </div>
                                  )}
                                  {device.highest_incident_severity && (
                                    <div className="mt-1 text-[9px] text-gray-500">
                                      <b>חומרת Incident:</b> {device.highest_incident_severity}
                                    </div>
                                  )}
                                  <div className="text-gray-400"><b>אתחול:</b> לא נחשף ב-ESET Connect</div>
                                </td>
                                <td className="px-1.5 py-2">
                                  {device.action_candidate ? (
                                    <button
                                      type="button"
                                      disabled
                                      title="Read-only: ביצוע יופעל רק לאחר הרשאות כתיבה ומיפוי פעולה מאושר."
                                      className="w-full px-1 py-1 rounded border border-gray-300 bg-gray-100 text-gray-400 cursor-not-allowed text-[9px]"
                                    >
                                      {device.action_candidate.label || "בצע"}
                                    </button>
                                  ) : <span className="text-gray-400">—</span>}
                                </td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {result.service_evidence_summary && (
                <div className="rounded border border-gray-200 bg-gray-50 px-2 py-1.5 text-[10px] text-gray-600" dir="rtl">
                  <b>Service evidence:</b>{" "}
                  {[
                    `Vulnerability rows: ${result.service_evidence_summary.vulnerability_rows ?? 0} / matched: ${result.service_evidence_summary.vulnerability_rows_matched ?? 0}`,
                    `Detection rows: ${result.service_evidence_summary.detection_rows ?? 0} / matched: ${result.service_evidence_summary.detection_rows_matched ?? 0}`,
                    `Incident rows: ${result.service_evidence_summary.incident_rows ?? 0} / matched: ${result.service_evidence_summary.incident_rows_matched ?? 0}`,
                    `Direct detections: ${result.service_evidence_summary.per_device_detection_devices_with_rows ?? 0}/${result.service_evidence_summary.per_device_detection_devices_queried ?? 0} devices`,
                    `Incidents via detections: ${result.service_evidence_summary.incident_rows_matched_via_detection ?? 0}`,
                    `Devices updated: ${result.service_evidence_summary.devices_updated ?? 0}`,
                  ].join(" · ")}
                  {Object.keys(result.service_evidence_summary.errors || {}).length > 0 && (
                    <div className="mt-1 text-amber-700">
                      {Object.entries(result.service_evidence_summary.errors).map(([path, category]) => (
                        <span key={path} className="ml-2">{path}: {category}</span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {result.snapshot_summary && (
                <div className="flex flex-wrap gap-3 text-gray-500 border-t pt-2">
                  <span>מכשירים: {result.snapshot_summary.total_devices ?? 0}</span>
                  <span>תקינים: {result.snapshot_summary.status_counts?.ok ?? 0}</span>
                  <span>דורשים טיפול: {result.snapshot_summary.status_counts?.attention_required ?? 0}</span>
                  <span>דורשים תשומת לב: {result.snapshot_summary.status_counts?.attention_recommended ?? 0}</span>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}


// ─── Acronis fleet mapping (DIGEMP-ACRONIS-FLEET-MAPPING-FRONTEND-099) ──────────
// Operator surface over the backend 098 company -> Acronis tenant mapping layer: one
// partner-scoped connection serves many Prodigy companies. Mapping writes carry only ids
// (company_id + stable tenant UUID) — no credential, secret_ref or token is rendered or
// submitted from here; the connection's secret_ref stays the only credential reference.
// Nothing is ever mapped automatically: no name-based preselection, an explicit company
// choice plus an explicit click are required for every write.

const ACRONIS_MAPPING_STATE_LABELS = {
  active: "ממופה ופעיל",
  tenant_not_in_discovery: "ה-Tenant לא נמצא בגילוי האחרון",
  company_deleted: "הלקוח נמחק — נדרשת הסרה",
  discovery_unavailable: "לא ניתן לאמת מול הגילוי (הגילוי נכשל)",
};

function acronisMappingStateLabel(state) {
  return ACRONIS_MAPPING_STATE_LABELS[state] || state;
}

export function AcronisTenantDiscoveryPanel({ agentId, connectionId, result, canManage, onRefresh }) {
  const { addNotification } = useNotification();
  // Tenant UUID -> explicitly chosen company id. Kept empty until the operator chooses; never
  // preselected from names (tenant_name/company name similarity is not a mapping signal).
  const [companyByTenant, setCompanyByTenant] = useState({});
  // Per-write pending keys ("map:<tenant>" / "unmap:<mapping id>"): concurrent writes on
  // different rows must not clear each other's pending indicator.
  const [pendingWrites, setPendingWrites] = useState({});

  const beginWrite = (key) => setPendingWrites((prev) => ({ ...prev, [key]: true }));
  const endWrite = (key) => setPendingWrites((prev) => {
    if (!prev[key]) return prev;
    const next = { ...prev };
    delete next[key];
    return next;
  });

  const { data: companies, isError: companiesFailed } = useQuery({
    queryKey: ["companies"],
    queryFn: () => companiesApi.list(),
    enabled: canManage,
    staleTime: 5 * 60 * 1000,
  });

  const mapMutation = useMutation({
    mutationFn: ({ companyId, tenantId }) =>
      agentConnectionsApi.mapAcronisTenant(agentId, connectionId, companyId, tenantId),
    onSuccess: (data, { tenantId }) => {
      endWrite(`map:${tenantId}`);
      setCompanyByTenant((prev) => ({ ...prev, [tenantId]: "" }));
      addNotification(
        data?.unchanged ? "המיפוי כבר היה קיים" : data?.created ? "המיפוי נוצר" : "המיפוי עודכן",
        "success",
      );
      onRefresh();
    },
    onError: (err, { tenantId }) => {
      endWrite(`map:${tenantId}`);
      // The server refuses duplicate/ambiguous bindings with an operator-readable Hebrew detail.
      addNotification(err?.response?.data?.detail || "שמירת המיפוי נכשלה", "error");
    },
  });

  const unmapMutation = useMutation({
    mutationFn: ({ mappingId }) => agentConnectionsApi.unmapAcronisTenant(agentId, connectionId, mappingId),
    onSuccess: (_data, { mappingId }) => {
      endWrite(`unmap:${mappingId}`);
      addNotification("המיפוי הוסר", "info");
      onRefresh();
    },
    onError: (err, { mappingId }) => {
      endWrite(`unmap:${mappingId}`);
      addNotification(err?.response?.data?.detail || "הסרת המיפוי נכשלה", "error");
    },
  });

  const tenants = Array.isArray(result.tenants) ? result.tenants : [];
  const mappings = Array.isArray(result.mappings) ? result.mappings : [];
  const discoveryFailed = result.status !== "ok" || !!result.error;
  const discoveredIds = new Set(tenants.map((t) => String(t.tenant_id).toLowerCase()));
  // Stored mappings whose tenant is not among the discovered tenants (or all of them when
  // discovery failed) stay explicitly visible — stale rows are never silently hidden.
  const unmatchedMappings = mappings.filter(
    (m) => !discoveredIds.has(String(m.external_tenant_id).toLowerCase()),
  );

  return (
    <>
      {discoveryFailed ? (
        <p className="text-red-700">
          {result.error || result.detail || "גילוי Tenants נכשל"}
        </p>
      ) : (
        <>
          <div className="flex flex-wrap gap-3 mb-2 items-center">
            <span className="font-medium">Tenants שנמצאו תחת ה-Tenant השותף</span>
            {result.is_mock && (
              <span className="text-[10px] rounded-full border px-1.5 py-0.5 bg-gray-100 text-gray-600 border-gray-200">
                נתוני Mock
              </span>
            )}
            {typeof result.mapped_tenant_count === "number" && (
              <span className="text-gray-600" data-testid="acronis-fleet-counts">
                ממופים: {result.mapped_tenant_count} · לא ממופים: {result.unmapped_tenant_count}
              </span>
            )}
            <span className="text-gray-600">
              חיבור שותף אחד משרת כמה לקוחות — כל לקוח ממופה ל-Tenant משלו. מיפוי ידני בלבד:
              שום מיפוי לא מוחל אוטומטית, לא לפי שם לקוח ולא לפי שם Tenant.
            </span>
          </div>
          <div
            className="max-h-72 overflow-y-auto rounded border border-teal-200 bg-white"
            data-testid="acronis-tenant-list"
          >
            {tenants.map((t) => {
              const mapping = t.mapping;
              const selectedCompany = companyByTenant[t.tenant_id] || "";
              const mapPending = !!pendingWrites[`map:${t.tenant_id}`];
              const unmapPending = mapping ? !!pendingWrites[`unmap:${mapping.mapping_id}`] : false;
              return (
                <div
                  key={t.tenant_id}
                  data-testid={`acronis-tenant-${t.tenant_id}`}
                  className="px-2 py-1.5 border-b last:border-0 flex flex-wrap gap-2 items-center"
                >
                  <span className="font-mono text-gray-800" dir="ltr">{t.tenant_id}</span>
                  <span className="text-gray-700">{t.tenant_name || "—"}</span>
                  {t.kind && (
                    <span className="text-[10px] rounded bg-gray-100 text-gray-600 px-1.5 py-0.5 whitespace-nowrap">{t.kind}</span>
                  )}
                  {mapping ? (
                    <>
                      <span className="text-[10px] rounded-full border px-1.5 py-0.5 bg-teal-100 text-teal-800 border-teal-300">
                        ממופה ללקוח: {mapping.company_name || mapping.company_id || "לקוח לא ידוע"}
                      </span>
                      {mapping.state !== "active" && (
                        <span className="text-[10px] rounded-full border px-1.5 py-0.5 bg-amber-100 text-amber-800 border-amber-300">
                          {acronisMappingStateLabel(mapping.state)}
                        </span>
                      )}
                      {canManage && (
                        <button
                          type="button"
                          onClick={() => {
                            beginWrite(`unmap:${mapping.mapping_id}`);
                            unmapMutation.mutate({ mappingId: mapping.mapping_id });
                          }}
                          disabled={unmapPending}
                          className="text-[10px] px-1.5 py-0.5 rounded border hover:bg-red-50 text-red-600 border-red-200 disabled:opacity-50"
                        >
                          {unmapPending ? "מסיר…" : "הסר מיפוי"}
                        </button>
                      )}
                    </>
                  ) : (
                    <>
                      <span className="text-[10px] rounded-full border px-1.5 py-0.5 bg-amber-50 text-amber-700 border-amber-300">
                        לא ממופה
                      </span>
                      {canManage && (companiesFailed ? (
                        <span className="text-red-700">לא ניתן לטעון את רשימת הלקוחות — לא ניתן למפות כרגע</span>
                      ) : (
                        <>
                          <select
                            aria-label={`בחירת לקוח עבור ${t.tenant_id}`}
                            value={selectedCompany}
                            onChange={(e) => setCompanyByTenant((prev) => ({ ...prev, [t.tenant_id]: e.target.value }))}
                            className="border rounded px-1 py-0.5 text-[11px]"
                          >
                            <option value="">בחירת לקוח…</option>
                            {(companies || []).map((co) => (
                              <option key={co.id} value={co.id}>{co.name}</option>
                            ))}
                          </select>
                          <button
                            type="button"
                            disabled={!selectedCompany || mapPending}
                            onClick={() => {
                              beginWrite(`map:${t.tenant_id}`);
                              mapMutation.mutate({ companyId: selectedCompany, tenantId: t.tenant_id });
                            }}
                            className="text-[10px] px-1.5 py-0.5 rounded border hover:bg-teal-50 text-teal-700 border-teal-300 disabled:opacity-50"
                          >
                            {mapPending ? "ממפה…" : "מפה ללקוח"}
                          </button>
                        </>
                      ))}
                    </>
                  )}
                </div>
              );
            })}
            {!tenants.length && (
              <p className="px-2 py-1.5 text-gray-500">לא נמצאו Tenants</p>
            )}
          </div>
        </>
      )}
      {!!unmatchedMappings.length && (
        <div className="mt-2 rounded border border-amber-200 bg-amber-50 p-2" data-testid="acronis-stale-mappings">
          <p className="font-medium text-amber-800 mb-1">מיפויים שמורים שאינם בגילוי הנוכחי</p>
          {unmatchedMappings.map((m) => {
            const unmapPending = !!pendingWrites[`unmap:${m.mapping_id}`];
            return (
              <div key={m.mapping_id} className="flex flex-wrap gap-2 items-center border-t border-amber-200 py-1 first:border-t-0">
                <span className="font-mono text-amber-900" dir="ltr">{m.external_tenant_id}</span>
                <span className="text-amber-900">{m.company_name || m.company_id || "לקוח לא ידוע"}</span>
                <span className="text-[10px] rounded-full border px-1.5 py-0.5 bg-amber-100 text-amber-800 border-amber-300">
                  {acronisMappingStateLabel(m.state)}
                </span>
                {canManage && (
                  <button
                    type="button"
                    onClick={() => {
                      beginWrite(`unmap:${m.mapping_id}`);
                      unmapMutation.mutate({ mappingId: m.mapping_id });
                    }}
                    disabled={unmapPending}
                    className="text-[10px] px-1.5 py-0.5 rounded border hover:bg-red-50 text-red-600 border-red-200 disabled:opacity-50"
                  >
                    {unmapPending ? "מסיר…" : "הסר מיפוי"}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

export function ConnectionsSection({ agentId, connections, canManage, agentClientId }) {
  const qc = useQueryClient();
  const { addNotification } = useNotification();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_CONN_FORM);

  // The connector catalog enriches every row below (capabilities, read-only, definition-only
  // warnings, required fields). The endpoint requires agents_manage, so for a view-only operator
  // it is refused by design — that refusal is stated, and an actual load failure gets the same
  // retry every other failed surface here has, instead of both silently thinning the rows out.
  const {
    data: connectorTypes,
    isError: connectorTypesFailed,
    error: connectorTypesError,
    refetch: refetchConnectorTypes,
  } = useQuery({
    queryKey: ["agentConnectorTypes"],
    queryFn: agentConnectionsApi.connectorTypes,
    staleTime: 5 * 60 * 1000,
  });
  // "the server reported no connections" and "the payload never carried the field" are different
  // states: only an actual array may be rendered as a count or as the explicit empty message.
  const connectionsSupplied = Array.isArray(connections);
  const catalogRefused = connectorTypesFailed && connectorTypesError?.response?.status === 403;
  const creatableTypes = connectorTypes?.filter((t) => t.agent_connection_backed);
  const systemTypeOptions = creatableTypes?.length
    ? creatableTypes.map((t) => ({ value: t.system_type, label: t.display_name }))
    : FALLBACK_SYSTEM_TYPE_OPTIONS;
  const connectorById = Object.fromEntries((connectorTypes || []).map((t) => [t.connector_id, t]));

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["agent", agentId] });
    // A connection test/create/delete changes the stored evidence the workspace readiness section reads.
    qc.invalidateQueries({ queryKey: connectionReadinessQueryKey(agentId) });
  };

  const createMutation = useMutation({
    mutationFn: (data) => agentConnectionsApi.create(agentId, data),
    onSuccess: () => {
      addNotification("חיבור נוצר בהצלחה", "success");
      invalidate();
      setShowForm(false);
      setForm(EMPTY_CONN_FORM);
    },
    onError: (err) => addNotification(err?.response?.data?.detail || "שגיאה ביצירת חיבור", "error"),
  });

  const deleteMutation = useMutation({
    mutationFn: (connectionId) => agentConnectionsApi.remove(agentId, connectionId),
    onSuccess: () => {
      addNotification("החיבור נמחק", "info");
      invalidate();
    },
    onError: (err) => addNotification(err?.response?.data?.detail || "שגיאה במחיקת חיבור", "error"),
  });

  const [testingId, setTestingId] = useState(null);
  const [testResults, setTestResults] = useState({});
  const [discoveringId, setDiscoveringId] = useState(null);
  const [capabilityResults, setCapabilityResults] = useState({});
  const testMutation = useMutation({
    mutationFn: (connectionId) => agentConnectionsApi.test(agentId, connectionId),
    onSuccess: (data, connectionId) => {
      // testingId is a single shared slot: testing a second connection while the first's request
      // is still in flight otherwise let either resolution wipe the OTHER connection's "testing"
      // indicator early, re-enabling its button before that request actually finished.
      setTestingId((current) => (current === connectionId ? null : current));
      setTestResults((prev) => ({ ...prev, [connectionId]: data }));
      invalidate();
    },
    onError: (err, connectionId) => {
      setTestingId((current) => (current === connectionId ? null : current));
      const msg = err?.response?.data?.detail || "שגיאת תקשורת";
      setTestResults((prev) => ({ ...prev, [connectionId]: { status: "error", detail: msg } }));
    },
  });

  const capabilityMutation = useMutation({
    mutationFn: (connectionId) => agentConnectionsApi.capabilities(agentId, connectionId),
    onSuccess: (data, connectionId) => {
      setDiscoveringId((current) => (current === connectionId ? null : current));
      setCapabilityResults((prev) => ({ ...prev, [connectionId]: data }));
    },
    onError: (err, connectionId) => {
      setDiscoveringId((current) => (current === connectionId ? null : current));
      const msg = err?.response?.data?.detail || "לא ניתן לטעון יכולות";
      setCapabilityResults((prev) => ({ ...prev, [connectionId]: { error: msg } }));
    },
  });

  // ACRONIS-CONNECTOR-001 (verified handoff slice): read-only tenant discovery, separate slot
  // from ESET's capability-discovery mutation above — different connector, different shape
  // (a tenant list to manually map from, not a learned-capability catalog).
  const [discoveringTenantsId, setDiscoveringTenantsId] = useState(null);
  const [tenantDiscoveryResults, setTenantDiscoveryResults] = useState({});
  const tenantDiscoveryMutation = useMutation({
    mutationFn: (connectionId) => agentConnectionsApi.acronisTenants(agentId, connectionId),
    onSuccess: (data, connectionId) => {
      setDiscoveringTenantsId((current) => (current === connectionId ? null : current));
      setTenantDiscoveryResults((prev) => ({ ...prev, [connectionId]: data }));
    },
    onError: (err, connectionId) => {
      setDiscoveringTenantsId((current) => (current === connectionId ? null : current));
      const msg = err?.response?.data?.detail || "לא ניתן לגלות Tenants";
      setTenantDiscoveryResults((prev) => ({ ...prev, [connectionId]: { error: msg } }));
    },
  });

  const submit = (e) => {
    e.preventDefault();
    const payload = { ...form };
    if (!payload.tenant_id) delete payload.tenant_id;
    if (!payload.client_id_app) delete payload.client_id_app;
    if (!payload.secret_ref) delete payload.secret_ref;
    if (!payload.external_tenant_id) delete payload.external_tenant_id;
    if (!payload.partner_tenant_id) delete payload.partner_tenant_id;
    createMutation.mutate(payload);
  };

  return (
    <div className="bg-white rounded-xl border shadow-sm p-5 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-gray-700">חיבורים ({connectionsSupplied ? connections.length : "—"})</h3>
        {canManage && (
          <button
            type="button"
            onClick={() => setShowForm((v) => !v)}
            className="text-xs text-brand-600 hover:underline"
          >
            {showForm ? "ביטול" : "+ הוסף חיבור"}
          </button>
        )}
      </div>

      {catalogRefused && (
        <p className="text-xs text-gray-500">
          מידע מורחב על המחברים (יכולות, שדות חובה ואזהרות תצורה) נטען לבעלי הרשאת ניהול חיבורים בלבד —
          רשומות החיבורים ומצבן מוצגים כפי שנמסרו על-ידי השרת.
        </p>
      )}
      {connectorTypesFailed && !catalogRefused && (
        <p role="alert" className="text-xs text-red-600">
          טעינת פרטי המחברים נכשלה — ייתכן שחלק מהאזהרות על החיבורים שלהלן אינן מוצגות.
          <button type="button" onClick={() => refetchConnectorTypes()} className="mr-2 underline">נסה שוב</button>
        </p>
      )}

      {canManage && showForm && (() => {
        const selectedDefn = connectorById[form.system_type];
        const fieldLabel = (name, fallback) =>
          selectedDefn?.fields?.find((f) => f.name === name)?.label || fallback;
        return (
        <form onSubmit={submit} className="rounded-lg border bg-gray-50 p-3 space-y-2">
          <select
            value={form.system_type}
            onChange={(e) => setForm((f) => ({ ...f, system_type: e.target.value }))}
            className="w-full border rounded px-2 py-1.5 text-sm"
          >
            {systemTypeOptions.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          {selectedDefn && !selectedDefn.implemented && (
            <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded px-2 py-1">
              מחבר זה טרם מומש — ניתן ליצור ולשמור חיבור, אך בדיקת החיבור תמיד תדווח "לא מומש".
            </p>
          )}
          <input
            required
            value={form.display_name}
            onChange={(e) => setForm((f) => ({ ...f, display_name: e.target.value }))}
            placeholder="שם תצוגה *"
            className="w-full border rounded px-2 py-1.5 text-sm"
          />
          <input
            value={form.tenant_id}
            onChange={(e) => setForm((f) => ({ ...f, tenant_id: e.target.value }))}
            placeholder={fieldLabel("tenant_id", "מזהה ארגון (Tenant ID)")}
            className="w-full border rounded px-2 py-1.5 text-sm font-mono"
            dir="ltr"
          />
          <input
            value={form.client_id_app}
            onChange={(e) => setForm((f) => ({ ...f, client_id_app: e.target.value }))}
            placeholder={fieldLabel("client_id_app", "מזהה אפליקציה (Client ID)")}
            className="w-full border rounded px-2 py-1.5 text-sm font-mono"
            dir="ltr"
          />
          <input
            value={form.secret_ref}
            onChange={(e) => setForm((f) => ({ ...f, secret_ref: e.target.value }))}
            placeholder={`${fieldLabel("secret_ref", "הפניה לסוד")}: kv:<שם> או env:PRODIGY_SECRET_<שם>`}
            className="w-full border rounded px-2 py-1.5 text-sm font-mono"
            dir="ltr"
          />
          {selectedDefn?.fields?.some((f) => f.name === "partner_tenant_id") && (
            <>
              <input
                value={form.partner_tenant_id}
                onChange={(e) => setForm((f) => ({ ...f, partner_tenant_id: e.target.value }))}
                placeholder={fieldLabel("partner_tenant_id", "מזהה Tenant שותף")}
                className="w-full border rounded px-2 py-1.5 text-sm font-mono"
                dir="ltr"
              />
              <p className="text-xs text-gray-500">
                מזהה ה-Tenant השותף שהאישורים מוגדרים תחתיו — משמש כשורש לגילוי Tenants (הכפתור
                "גלה Tenants" למטה). ללא ערך זה, גילוי ה-Tenants ייכשל במפורש.
              </p>
            </>
          )}
          {selectedDefn?.fields?.some((f) => f.name === "external_tenant_id") && (
            <>
              <input
                value={form.external_tenant_id}
                onChange={(e) => setForm((f) => ({ ...f, external_tenant_id: e.target.value }))}
                placeholder={fieldLabel("external_tenant_id", "מזהה Tenant חיצוני")}
                className="w-full border rounded px-2 py-1.5 text-sm font-mono"
                dir="ltr"
              />
              <p className="text-xs text-gray-500">
                מזהה יציב (לא שם) של ה-Tenant אצל הספק שנבחר עבור חיבור זה — תוצאה של גילוי
                Tenants, לא קלט לבדיקת החיבור עצמה.
              </p>
            </>
          )}
          {createMutation.error && (
            <p className="text-xs text-red-600">{createMutation.error?.response?.data?.detail || "שגיאה בשמירה"}</p>
          )}
          <button
            type="submit"
            disabled={createMutation.isPending}
            className="bg-brand-600 hover:bg-brand-700 text-white px-3 py-1.5 rounded text-xs font-medium disabled:opacity-50"
          >
            {createMutation.isPending ? "שומר..." : "הוסף חיבור"}
          </button>
        </form>
        );
      })()}

      {!connectionsSupplied && !showForm ? (
        <p className="text-sm text-gray-400 italic">
          מצב החיבורים טרם דווח על-ידי המערכת (לא ידוע) — אין להסיק מכך שאין חיבורים.
        </p>
      ) : !connections.length && !showForm ? (
        <div>
          <p className="text-sm text-gray-400">אין חיבורים מוגדרים לסוכן זה</p>
          {canManage && (
            <p className="text-xs text-gray-400 mt-1">אפשר להוסיף חיבור חדש מהכפתור שלמעלה.</p>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {(connections || []).map((c) => {
            const testResult = testResults[c.id];
            const defn = connectorById[c.system_type];
            // Mirrors the server's own presence-only credentials_pending derivation
            // (agents.py:570 _connection_credentials_pending): a required field is "set" by
            // presence alone — no value is read, compared or rendered.
            const requiredFields = Array.isArray(defn?.fields)
              ? defn.fields.filter((f) => f.required).map((f) => f.name)
              : null;
            const credentialsPending =
              requiredFields !== null &&
              requiredFields.length > 0 &&
              requiredFields.some((name) => !c[name]);
            const isPinnedToOtherCustomer = c.customer_id && String(c.customer_id) !== String(agentClientId || "");
            const capabilityResult = capabilityResults[c.id];
            const tenantDiscoveryResult = tenantDiscoveryResults[c.id];
            return (
              <div key={c.id} className="rounded-lg border bg-gray-50 overflow-hidden">
                <div className="flex items-center justify-between p-3">
                  <div>
                    <p className="text-sm font-medium text-gray-800">
                      {defn?.display_name || CONNECTION_TYPE_LABELS[c.system_type] || c.system_type}
                      {c.display_name && ` — ${c.display_name}`}
                    </p>
                    {c.tenant_id && (
                      <p className="text-xs text-gray-500 font-mono" dir="ltr">{c.tenant_id}</p>
                    )}
                    <div className="flex flex-wrap items-center gap-1 mt-1">
                      {defn?.read_only && (
                        <span className="text-[10px] text-gray-500 bg-gray-100 border rounded-full px-1.5 py-0.5">קריאה בלבד</span>
                      )}
                      {credentialsPending && (
                        <span
                          className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-1.5 py-0.5"
                          title="שדות החובה של המחבר טרם הוגדרו על חיבור זה — השרת מדווח על החיבור כחסר פרטי גישה, והחיבור לא ישמש את העובד עד שאדמין ישלים את ההגדרה. בדיקת הנוכחות בלבד; שום סוד לא נוצר, לא נקרא ולא הוצג."
                        >
                          חסרים פרטי גישה — לא ישמש עד השלמה
                        </span>
                      )}
                      {defn?.implemented === false && (
                        <span className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-1.5 py-0.5">טרם מומש</span>
                      )}
                      {isPinnedToOtherCustomer && (
                        <span className="text-[10px] text-purple-700 bg-purple-50 border border-purple-200 rounded-full px-1.5 py-0.5" title={c.customer_id}>
                          מוגבל ללקוח ספציפי
                        </span>
                      )}
                      {defn?.fields?.some((f) => f.name === "partner_tenant_id") && !c.partner_tenant_id && (
                        <span className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-1.5 py-0.5">
                          אין Tenant שותף — גילוי Tenants ייכשל
                        </span>
                      )}
                      {defn?.fields?.some((f) => f.name === "external_tenant_id") && (
                        c.external_tenant_id ? (
                          <span
                            className="text-[10px] text-teal-700 bg-teal-50 border border-teal-200 rounded-full px-1.5 py-0.5 font-mono"
                            title={c.external_tenant_id}
                          >
                            ממופה: {c.external_tenant_id}
                          </span>
                        ) : (
                          <span className="text-[10px] text-red-700 bg-red-50 border border-red-200 rounded-full px-1.5 py-0.5">
                            לא ממופה — נדרש מיפוי Tenant
                          </span>
                        )
                      )}
                      {(defn?.capabilities || []).map((cap) => (
                        <span key={cap} className="text-[10px] text-blue-700 bg-blue-50 border border-blue-200 rounded-full px-1.5 py-0.5">
                          {CAPABILITY_LABELS[cap] || cap}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {c.last_test_status === "ok" ? (
                      <span className="text-xs text-green-600 bg-green-50 border border-green-200 rounded-full px-2 py-0.5">תקין</span>
                    ) : c.last_test_status === "error" ? (
                      <span className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-full px-2 py-0.5">שגיאה</span>
                    ) : (
                      <span className="text-xs text-gray-400 bg-gray-100 border rounded-full px-2 py-0.5">לא נבדק</span>
                    )}
                    {canManage && (
                      <button
                        type="button"
                        onClick={() => { setTestingId(c.id); testMutation.mutate(c.id); }}
                        disabled={testingId === c.id}
                        className="text-xs px-2 py-1 rounded border hover:bg-blue-50 text-blue-600 border-blue-200 disabled:opacity-50"
                      >
                        {testingId === c.id ? "בודק…" : "בדוק"}
                      </button>
                    )}
                    {canManage && c.system_type === "eset_protect" && (
                      <button
                        type="button"
                        onClick={() => { setDiscoveringId(c.id); capabilityMutation.mutate(c.id); }}
                        disabled={discoveringId === c.id}
                        className="text-xs px-2 py-1 rounded border hover:bg-violet-50 text-violet-600 border-violet-200 disabled:opacity-50"
                        title="לומד רק את מסמך ה-OpenAPI של ESET; לא מתבצעת קריאה למחשבים או פעולה חיה"
                      >
                        {discoveringId === c.id ? "לומד…" : "למד יכולות"}
                      </button>
                    )}
                    {canManage && c.system_type === "acronis_backup" && (
                      <button
                        type="button"
                        onClick={() => { setDiscoveringTenantsId(c.id); tenantDiscoveryMutation.mutate(c.id); }}
                        disabled={discoveringTenantsId === c.id}
                        className="text-xs px-2 py-1 rounded border hover:bg-teal-50 text-teal-600 border-teal-200 disabled:opacity-50"
                        title="קריאה בלבד: מציג Tenants תחת ה-Tenant השותף שהוגדר, למיפוי ידני בלבד"
                      >
                        {discoveringTenantsId === c.id ? "מגלה…" : "גלה Tenants"}
                      </button>
                    )}
                    {canManage && (
                      <button
                        type="button"
                        onClick={() => {
                          if (window.confirm(`למחוק את החיבור "${c.display_name}"?`)) {
                            deleteMutation.mutate(c.id);
                          }
                        }}
                        disabled={deleteMutation.isPending && deleteMutation.variables === c.id}
                        className="text-xs px-2 py-1 rounded border hover:bg-red-50 text-red-500 border-red-200 disabled:opacity-50"
                      >
                        מחק
                      </button>
                    )}
                  </div>
                </div>
                {testResult && (
                  <div className={`px-3 py-1.5 text-xs border-t ${
                    testResult.status === "ok"
                      ? "bg-green-50 text-green-700 border-green-200"
                      : "bg-red-50 text-red-700 border-red-200"
                  }`}>
                    {testResult.status === "ok" ? "✓ בדיקה עברה בהצלחה" : `✗ ${testResult.detail || "בדיקה נכשלה"}`}
                  </div>
                )}
                {capabilityResult && (
                  <div className="px-3 py-2 text-xs border-t bg-violet-50 border-violet-200 text-violet-900">
                    {capabilityResult.error ? (
                      <p className="text-red-700">{capabilityResult.error}</p>
                    ) : (
                      <>
                        <div className="flex flex-wrap gap-3 mb-2">
                          <span className="font-medium">כל יכולות ESET שנלמדו מ-OpenAPI בלבד</span>
                          <span>משפחות API: {capabilityResult.loaded_product_areas?.length ?? 0}/{capabilityResult.documented_product_areas?.length ?? 0}</span>
                          <span>קריאה: {capabilityResult.counts?.read_only ?? 0}</span>
                          <span>פעולה/כתיבה: {capabilityResult.counts?.write_or_action ?? 0}</span>
                          <span>לא מסווג: {capabilityResult.counts?.ambiguous ?? 0}</span>
                          <span className="text-green-700">ביצוע אוטונומי: כבוי</span>
                        </div>
                        {capabilityResult.promotion_counts && (
                          <div className="flex flex-wrap gap-1.5 mb-2">
                            {Object.entries(PROMOTION_STATUS_LABELS).map(([status, label]) => (
                              <span
                                key={status}
                                className={`text-[10px] rounded-full border px-1.5 py-0.5 ${PROMOTION_STATUS_BADGE_CLASS[status]}`}
                              >
                                {label}: {capabilityResult.promotion_counts[status] ?? 0}
                              </span>
                            ))}
                          </div>
                        )}
                        {!!Object.keys(capabilityResult.failed_product_areas || {}).length && (
                          <div className="mb-2 rounded border border-amber-200 bg-amber-50 p-2 text-amber-800">
                            לא נטענו כרגע: {Object.keys(capabilityResult.failed_product_areas).join(", ")}
                          </div>
                        )}
                        <div className="max-h-72 overflow-y-auto rounded border border-violet-200 bg-white">
                          {(capabilityResult.capabilities || []).map((cap) => (
                            <div key={`${cap.product_area}:${cap.method}:${cap.path}`} className="px-2 py-1.5 border-b last:border-0 flex gap-2 items-start">
                              <span className="text-[10px] rounded bg-gray-100 text-gray-600 px-1.5 py-0.5 whitespace-nowrap">{cap.product_area}</span>
                              <span className={`font-mono font-semibold ${
                                cap.safety === "read_only" ? "text-green-700" :
                                cap.safety === "write_or_action" ? "text-red-700" : "text-amber-700"
                              }`}>{cap.method}</span>
                              <span className="font-mono text-gray-700" dir="ltr">{cap.path}</span>
                              {cap.promotion_status && (
                                <span className={`text-[10px] rounded-full border px-1.5 py-0.5 whitespace-nowrap ${PROMOTION_STATUS_BADGE_CLASS[cap.promotion_status] || ""}`}>
                                  {PROMOTION_STATUS_LABELS[cap.promotion_status] || cap.promotion_status}
                                  {cap.promoted_tool_name ? ` (${cap.promoted_tool_name})` : ""}
                                </span>
                              )}
                              {cap.summary && <span className="text-gray-500">{cap.summary}</span>}
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                )}
                {tenantDiscoveryResult && (
                  <div className="px-3 py-2 text-xs border-t bg-teal-50 border-teal-200 text-teal-900">
                    <AcronisTenantDiscoveryPanel
                      agentId={agentId}
                      connectionId={c.id}
                      result={tenantDiscoveryResult}
                      canManage={canManage}
                      onRefresh={() => { setDiscoveringTenantsId(c.id); tenantDiscoveryMutation.mutate(c.id); }}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── StatsRow ──────────────────────────────────────────────────────────────────

export const RECENT_RUNS_LIMIT = 20;   // how many runs the page loads; StatsRow counts only these

export function StatsRow({ runs }) {
  if (!runs?.length) return null;

  const total     = runs.length;
  const completed = runs.filter((r) => ["completed","resolved"].includes(r.status)).length;
  const failed    = runs.filter((r) => r.status === "failed").length;
  const pending   = runs.filter((r) => ["awaiting_technician_review","awaiting_approval"].includes(r.status)).length;

  const stat = (label, value, color = "text-gray-800") => (
    <div className="text-center">
      <p className={`text-xl font-bold ${color}`}>{value}</p>
      <p className="text-xs text-gray-500">{label}</p>
    </div>
  );

  return (
    <div className="bg-white rounded-xl border shadow-sm p-4">
      <div className="flex justify-around">
        {stat("ריצות אחרונות", total)}
        {stat("הושלמו", completed, "text-green-600")}
        {stat("ממתינות", pending, "text-orange-600")}
        {stat("נכשלו", failed, "text-red-600")}
      </div>
      {total >= RECENT_RUNS_LIMIT && (
        <p className="text-[11px] text-gray-400 text-center mt-2">המדדים מחושבים על {RECENT_RUNS_LIMIT} הריצות האחרונות בלבד</p>
      )}
    </div>
  );
}

// ─── RecentRunsSection ─────────────────────────────────────────────────────────

export function RecentRunsSection({ runs, isLoading, isError = false, agentId }) {
  if (isLoading) {
    return (
      <div className="bg-white rounded-xl border shadow-sm p-5">
        <h3 className="font-semibold text-gray-700 mb-3">ריצות אחרונות</h3>
        <div className="flex justify-center py-6">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-violet-600" />
        </div>
      </div>
    );
  }

  if (isError && !runs?.length) {
    return (
      <div className="bg-white rounded-xl border shadow-sm p-5">
        <h3 className="font-semibold text-gray-700 mb-3">ריצות אחרונות</h3>
        <p role="alert" className="text-sm text-red-600 text-center py-4">לא ניתן לטעון את הריצות</p>
      </div>
    );
  }

  if (!runs?.length) {
    return (
      <div className="bg-white rounded-xl border shadow-sm p-5">
        <h3 className="font-semibold text-gray-700 mb-3">ריצות אחרונות</h3>
        <p className="text-sm text-gray-400 text-center py-4">אין ריצות עדיין</p>
        <p className="text-xs text-gray-400 text-center">
          ריצות יופיעו כאן ברגע שהעובד יתחיל לעבוד — על קריאה או על בדיקה.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
      <div className="px-5 py-4 border-b">
        <h3 className="font-semibold text-gray-700">ריצות אחרונות ({runs.length})</h3>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-gray-500 text-xs uppercase border-b">
          <tr>
            <th className="text-right px-4 py-3">קריאה</th>
            <th className="text-right px-4 py-3">סטטוס</th>
            <th className="text-right px-4 py-3">ביטחון</th>
            <th className="text-right px-4 py-3">עלות</th>
            <th className="text-right px-4 py-3">התחיל</th>
            <th className="px-4 py-3" />
          </tr>
        </thead>
        <tbody>
          {runs.map((run) => (
            <tr key={run.id} className="border-b last:border-0 hover:bg-gray-50">
              <td className="px-4 py-3">
                {run.ticket_number ? (
                  <Link
                    to={`/tickets/${run.ticket_id}`}
                    className="text-brand-600 hover:underline font-medium"
                  >
                    #{run.ticket_number}
                  </Link>
                ) : (
                  <span className="text-gray-400 font-mono text-xs">{String(run.ticket_id).slice(0, 8)}…</span>
                )}
                {run.ticket_title && (
                  <p className="text-xs text-gray-500 mt-0.5 max-w-xs truncate">{run.ticket_title}</p>
                )}
              </td>
              <td className="px-4 py-3">
                <StatusChip status={run.status} />
              </td>
              <td className="px-4 py-3 text-gray-600">
                {/* The `ביטחון` column is the only place this table states a confidence, and on the
                    pilot default (no ANALYSIS_PROVIDER) every figure in it is the deterministic
                    mock's derived number (`helpdesk_analyst.py`: MockAnalysisAdapter, capped at 70 so
                    it reads as a real "medium" signal rather than as "nothing ran"). The column
                    carries the shared short form because a table cell has no room for the full
                    banner `AgentRunDetailPage` draws. */}
                {degradedAnalysisLabel(run.analysis?.degraded) && (
                  <span
                    data-testid={`agent-run-confidence-degraded-${run.id}`}
                    className="inline-block text-[10px] font-medium leading-none bg-amber-100 text-amber-800 border border-amber-200 rounded-full px-1.5 py-0.5 mb-1"
                  >
                    {degradedAnalysisLabel(run.analysis?.degraded)}
                  </span>
                )}
                <span className="block">
                  {run.analysis?.confidence != null
                    ? `${run.analysis.confidence}%`
                    : "—"}
                </span>
              </td>
              <td className="px-4 py-3 text-gray-600 font-mono text-xs">
                {run.estimated_cost != null
                  ? `$${Number(run.estimated_cost).toFixed(4)}`
                  : "—"}
              </td>
              <td className="px-4 py-3 text-gray-500 text-xs" title={fmtAbsTime(run.started_at || run.created_at)}>
                {fmtRelTime(run.started_at || run.created_at)}
              </td>
              <td className="px-4 py-3">
                <Link
                  to={`/agent-runs/${run.id}`}
                  aria-label={`פתח ריצה ${run.ticket_number ? `#${run.ticket_number}` : String(run.id).slice(0, 8)}`}
                  className="text-xs text-gray-500 hover:text-brand-600 hover:underline"
                >
                  פרטים
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {agentId && (
        <div className="px-5 py-3 border-t bg-gray-50 text-center">
          <Link
            to={`/agent-runs?agent_id=${agentId}`}
            className="text-xs text-brand-600 hover:underline"
          >
            כל הריצות ←
          </Link>
        </div>
      )}
    </div>
  );
}

// ─── EditModal ─────────────────────────────────────────────────────────────────

export function EditModal({ agent, onSave, onCancel, saving, error }) {
  const [form, setForm] = useState({
    name:            agent.name,
    description:     agent.description || "",
    model:           agent.model,
    system_prompt:   agent.system_prompt || "",
    is_enabled:      agent.is_enabled,
    autonomy_level:  agent.autonomy_level || "approval_required",
    permission_tier: agent.permission_tier || "tier_1",
    allowed_tools:   agent.allowed_tools || null,
    // Employee identity / persona (EMPLOYEE-IDENTITY-PERSONA-001)
    role_title:          agent.role_title || "",
    avatar_key:          agent.avatar_key || "",
    avatar_color:        agent.avatar_color || "",
    availability_status: agent.availability_status || "available",
    personality:         agent.personality || "",
    specialties_text:    Array.isArray(agent.specialties) ? agent.specialties.join(", ") : "",
    intro_text:          agent.intro_text || "",
  });

  const { data: allTools = [] } = useQuery({
    queryKey: ["agent-tools"],
    queryFn: agentsApi.listTools,
    staleTime: 300000,
  });
  const tierTools = allTools.filter((t) => t.allowed_tiers.includes(form.permission_tier));
  const allTierNames = tierTools.map((t) => t.name);
  const isUnrestricted = isUnrestrictedAllowlist(form.allowed_tools);
  const selectedSet = isUnrestricted
    ? new Set(allTierNames)
    : new Set(form.allowed_tools);

  const submit = (e) => {
    e.preventDefault();
    onSave(buildAgentUpdatePayload(form));
  };

  // No Escape handler previously -- a keyboard user had to tab to the "ביטול" button.
  useEffect(() => {
    const onKeyDown = (e) => { if (e.key === "Escape") onCancel(); };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onCancel]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <form
        onSubmit={submit}
        role="dialog" aria-modal="true" aria-labelledby="edit-agent-modal-title"
        className="bg-white rounded-xl shadow-xl border p-6 w-[36rem] max-h-[90vh] overflow-y-auto space-y-4"
        dir="rtl"
      >
        <h3 id="edit-agent-modal-title" className="text-base font-semibold text-gray-800">ערוך סוכן</h3>

        <div>
          <label className="block text-xs text-gray-500 mb-1">שם *</label>
          <input
            required
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            className="w-full border rounded-lg px-3 py-2 text-sm"
          />
        </div>

        <div>
          <label className="block text-xs text-gray-500 mb-1">תיאור</label>
          <textarea
            rows={2}
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            className="w-full border rounded-lg px-3 py-2 text-sm"
          />
        </div>

        <fieldset className="rounded-lg border border-violet-100 bg-violet-50/40 p-3 space-y-3">
          <legend className="text-xs font-semibold text-violet-700 px-1">זהות העובד הדיגיטלי</legend>
          <div className="flex items-center gap-3">
            <EmployeeAvatar name={form.name} avatarKey={form.avatar_key} avatarColor={form.avatar_color} status={form.availability_status} isEnabled={form.is_enabled} size="md" showStatus />
            <div className="grid grid-cols-3 gap-2 flex-1">
              <div>
                <label className="block text-xs text-gray-500 mb-1">אווטאר (אימוג'י)</label>
                <input
                  value={form.avatar_key}
                  maxLength={16}
                  onChange={(e) => setForm((f) => ({ ...f, avatar_key: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  placeholder="🛡️"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">צבע</label>
                <select
                  value={form.avatar_color}
                  onChange={(e) => setForm((f) => ({ ...f, avatar_color: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2 text-sm bg-white"
                >
                  <option value="">ברירת מחדל</option>
                  {["blue", "violet", "green", "amber", "rose", "teal", "slate"].map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">זמינות</label>
                <select
                  value={form.availability_status}
                  onChange={(e) => setForm((f) => ({ ...f, availability_status: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2 text-sm bg-white"
                >
                  {Object.entries(STATUS_META).map(([value, meta]) => (
                    <option key={value} value={value}>{meta.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">תפקיד</label>
            <input
              value={form.role_title}
              maxLength={120}
              onChange={(e) => setForm((f) => ({ ...f, role_title: e.target.value }))}
              className="w-full border rounded-lg px-3 py-2 text-sm"
              placeholder="למשל: טכנאי אבטחת מידע"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">תחומי התמחות (מופרדים בפסיק)</label>
            <input
              value={form.specialties_text}
              onChange={(e) => setForm((f) => ({ ...f, specialties_text: e.target.value }))}
              className="w-full border rounded-lg px-3 py-2 text-sm"
              placeholder="ESET, רשת, Microsoft 365"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">אישיות וסגנון עבודה</label>
            <textarea
              rows={2}
              value={form.personality}
              onChange={(e) => setForm((f) => ({ ...f, personality: e.target.value }))}
              className="w-full border rounded-lg px-3 py-2 text-sm"
              placeholder="ענייני, שקט, מסביר בקצרה ומציע צעד הבא"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">משפט פתיחה בשיחה</label>
            <textarea
              rows={2}
              value={form.intro_text}
              onChange={(e) => setForm((f) => ({ ...f, intro_text: e.target.value }))}
              className="w-full border rounded-lg px-3 py-2 text-sm"
              placeholder="היי, אני דנה מאבטחת מידע. במה אפשר לעזור?"
            />
          </div>
        </fieldset>

        <div>
          <label className="block text-xs text-gray-500 mb-1">מודל *</label>
          <input
            required
            value={form.model}
            onChange={(e) => setForm((f) => ({ ...f, model: e.target.value }))}
            className="w-full border rounded-lg px-3 py-2 text-sm font-mono"
            dir="ltr"
          />
        </div>

        <div>
          <label className="block text-xs text-gray-500 mb-1">System prompt</label>
          <textarea
            rows={6}
            value={form.system_prompt}
            onChange={(e) => setForm((f) => ({ ...f, system_prompt: e.target.value }))}
            className="w-full border rounded-lg px-3 py-2 text-sm font-mono"
            dir="ltr"
          />
        </div>

        <div>
          <label className="block text-xs text-gray-500 mb-1">רמת הרשאה (Permission Tier)</label>
          <select
            value={form.permission_tier}
            onChange={(e) => setForm((f) => ({ ...f, permission_tier: e.target.value, allowed_tools: null }))}
            className="w-full border rounded-lg px-3 py-2 text-sm bg-white"
            dir="rtl"
          >
            <option value="tier_1">קריאה בלבד — ברירת מחדל</option>
            <option value="tier_2">תפעולי — פעולות עם אישור</option>
            <option value="tier_3">מוגבה — אישור חזק נדרש</option>
          </select>
          {tierTools.length > 0 && (
            <div className="mt-2 rounded-lg border border-gray-200 bg-gray-50 p-2.5 space-y-1">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[11px] font-medium text-gray-500">
                  כלים מותרים
                  {!isUnrestricted && (
                    <span className="mr-1.5 px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-[10px]">
                      {form.allowed_tools.length}/{allTierNames.length}
                    </span>
                  )}
                </span>
                <div className="flex gap-2 text-[11px]">
                  <button type="button" onClick={() => setForm((f) => ({ ...f, allowed_tools: null }))} className="text-blue-600 hover:underline">כל הכלים</button>
                </div>
              </div>
              {tierTools.map((t) => {
                const isLastSelected = selectedSet.size === 1 && selectedSet.has(t.name);
                return (
                <label
                  key={t.name}
                  className="flex items-center gap-2 text-xs cursor-pointer hover:bg-white rounded px-1 py-0.5 transition-colors"
                  title={isLastSelected ? "לא ניתן לבטל את הכלי האחרון — רשימה ריקה פירושה גישה לכל הכלים, לא לאף אחד" : undefined}
                >
                  <input
                    type="checkbox"
                    checked={selectedSet.has(t.name)}
                    disabled={isLastSelected}
                    onChange={() => {
                      const next = new Set(isUnrestricted ? allTierNames : form.allowed_tools);
                      if (next.has(t.name)) next.delete(t.name); else next.add(t.name);
                      // allowed_tools=[] is NOT "no tools" on the backend — it means the same as
                      // null, unrestricted (autonomy_policy only enforces when non-empty).
                      if (next.size === 0) return;
                      const arr = [...next];
                      setForm((f) => ({ ...f, allowed_tools: arr.length === allTierNames.length ? null : arr }));
                    }}
                    className="h-3.5 w-3.5 rounded text-blue-600 cursor-pointer disabled:cursor-not-allowed disabled:opacity-60"
                  />
                  <span className="text-gray-700 flex-1">{t.name}</span>
                  {t.strong_approval_required && <span className="px-1 py-0.5 rounded bg-purple-100 text-purple-700 text-[10px]">Step-up</span>}
                  {t.approval_required && !t.strong_approval_required && <span className="px-1 py-0.5 rounded bg-orange-100 text-orange-700 text-[10px]">אישור</span>}
                </label>
                );
              })}
            </div>
          )}
        </div>

        <div>
          <label className="block text-xs text-gray-500 mb-1">רמת אוטונומיה</label>
          <select
            value={form.autonomy_level}
            onChange={(e) => setForm((f) => ({ ...f, autonomy_level: e.target.value }))}
            className="w-full border rounded-lg px-3 py-2 text-sm bg-white"
            dir="rtl"
          >
            <option value="approval_required">דורש אישור (ברירת מחדל)</option>
            <option value="supervised">מפוקח</option>
            <option value="semi_autonomous">חצי אוטונומי</option>
            <option value="autonomous">אוטונומי מלא</option>
          </select>
        </div>

        <div className="flex items-center gap-2">
          <input
            id="is_enabled"
            type="checkbox"
            checked={form.is_enabled}
            onChange={(e) => setForm((f) => ({ ...f, is_enabled: e.target.checked }))}
            className="w-4 h-4 accent-brand-600"
          />
          <label htmlFor="is_enabled" className="text-sm text-gray-700">סוכן פעיל</label>
        </div>

        {error && <p className="text-red-600 text-sm">{error?.response?.data?.detail || "שגיאה בשמירה"}</p>}

        <div className="flex gap-2">
          <button
            type="submit"
            disabled={saving}
            className="bg-brand-600 hover:bg-brand-700 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50"
          >
            {saving ? "שומר..." : "שמור"}
          </button>
          <button type="button" onClick={onCancel} className="border px-4 py-2 rounded-lg text-sm hover:bg-gray-50">
            ביטול
          </button>
        </div>
      </form>
    </div>
  );
}

// ─── SkillsSection ────────────────────────────────────────────────────────────

function ScoreBar({ value }) {
  const pct = Math.min(100, Math.max(0, value));
  const color = pct >= 70 ? "bg-green-500" : pct >= 40 ? "bg-amber-500" : "bg-red-500";
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 bg-gray-100 rounded-full h-2 overflow-hidden">
        <div className={`h-2 rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs font-medium w-8 text-left">{Math.round(value)}</span>
    </div>
  );
}

function SkillsSection({ agentId }) {
  const { data: ev, isLoading, isError } = useQuery({
    queryKey: ["agent-evaluation", agentId],
    queryFn: () => skillsApi.agentEvaluation(agentId),
    staleTime: 120_000,
  });

  if (isLoading) return null;
  if (isError) {
    return (
      <div role="alert" className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm">
        שגיאה בטעינת הערכת המיומנויות
      </div>
    );
  }
  if (!ev) {
    return (
      <div className="bg-white rounded-xl border shadow-sm p-5 space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-gray-700">הערכת מיומנויות</h3>
          <Link to="/skills" className="text-xs text-brand-600 hover:underline">לוח מיומנויות ←</Link>
        </div>
        <p className="text-sm text-gray-400">אין עדיין הערכת מיומנויות לעובד זה — היא מבוססת על ריצות העבודה שלו.</p>
      </div>
    );
  }

  const measured = ev.has_outcome_evidence === true;

  return (
    <div className="bg-white rounded-xl border shadow-sm p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-gray-700">הערכת מיומנויות</h3>
        <div className="flex items-center gap-3">
          <Link to={`/agents/${agentId}/skills`} className="text-xs text-brand-600 hover:underline">מיומנויות העובד ←</Link>
          <Link to="/skills" className="text-xs text-brand-600 hover:underline">לוח מיומנויות ←</Link>
        </div>
      </div>

      {measured ? (
        <>
          <div className="flex items-center gap-4">
            <div className="text-center">
              <div className="text-3xl font-bold text-brand-600">{Math.round(ev.overall_score)}</div>
              <div className="text-xs text-gray-400">ציון כולל</div>
            </div>
            <div className="flex-1">
              <ScoreBar value={ev.overall_score} />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3 text-center text-xs">
            {[
              { label: "שיעור פתרון",  value: `${Math.round(ev.resolution_rate * 100)}%` },
              { label: "זמן ממוצע",    value: ev.avg_duration_s != null ? (ev.avg_duration_s < 60 ? `${Math.round(ev.avg_duration_s)}ש` : `${Math.round(ev.avg_duration_s / 60)}ד`) : "—" },
              { label: "עלות ממוצעת",  value: ev.avg_cost_usd != null ? `$${ev.avg_cost_usd.toFixed(4)}` : "—" },
            ].map(({ label, value }) => (
              <div key={label} className="bg-gray-50 rounded-lg py-2">
                <div className="font-semibold text-gray-700">{value}</div>
                <div className="text-gray-400 mt-0.5">{label}</div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <p className="text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
          טרם נמדד — אין עדיין ריצה שהסתיימה להערכה. {ev.pending_runs > 0 && `${ev.pending_runs} ריצות עדיין בטיפול.`}
        </p>
      )}

      {measured && ev.skill_breakdown?.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-xs text-gray-500 font-medium">קטגוריות</p>
          {ev.skill_breakdown.map((s) => (
            <div key={s.category} className="flex items-center gap-2">
              <span className="text-xs text-gray-600 w-28 truncate">{s.category}</span>
              <div className="flex-1 bg-gray-100 rounded-full h-1.5 overflow-hidden">
                <div className="h-1.5 rounded-full bg-brand-400"
                  style={{ width: `${Math.round(s.completion_rate * 100)}%` }} />
              </div>
              <span className="text-xs text-gray-400 w-8 text-left">{s.total}</span>
            </div>
          ))}
        </div>
      )}

      <p className="text-xs text-gray-400 text-left">
        {ev.total_runs} ריצות · {ev.completed_runs} הושלמו · {ev.failed_runs} נכשלו
      </p>
    </div>
  );
}

// ─── ScheduledChecksSection ───────────────────────────────────────────────────

const INTERVAL_OPTIONS = [
  { value: 1,   label: "כל שעה" },
  { value: 6,   label: "כל 6 שעות" },
  { value: 12,  label: "כל 12 שעות" },
  { value: 24,  label: "יומי (24ש)" },
  { value: 48,  label: "כל יומיים" },
  { value: 168, label: "שבועי" },
];

export function ScheduledChecksSection({ agentId, agentName, canManage }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { addNotification } = useNotification();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", interval_hours: 24 });
  const [runningId, setRunningId] = useState(null);
  const [firedId, setFiredId] = useState(null);

  const { data: allChecks = [], isLoading, isError, error: loadError } = useQuery({
    queryKey: ["scheduled-checks"],
    queryFn: scheduledChecksApi.list,
    refetchInterval: 30000,
  });

  const checks = allChecks.filter((c) => c.agent_id === agentId);

  const invalidate = () => qc.invalidateQueries({ queryKey: ["scheduled-checks"] });

  const createMutation = useMutation({
    mutationFn: (data) => scheduledChecksApi.create({ ...data, agent_id: agentId }),
    onSuccess: () => {
      addNotification("בדיקה מתוזמנת נוצרה בהצלחה", "success");
      invalidate();
      setShowForm(false);
      setForm({ name: "", description: "", interval_hours: 24 });
    },
    onError: (err) => addNotification(err?.response?.data?.detail || "שגיאה ביצירת בדיקה מתוזמנת", "error"),
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, is_enabled }) => scheduledChecksApi.update(id, { is_enabled }),
    onSuccess: (_data, { is_enabled }) => {
      addNotification(is_enabled ? "בדיקה הופעלה" : "בדיקה הושהתה", "info");
      invalidate();
    },
    onError: (err) => addNotification(err?.response?.data?.detail || "שגיאה בשינוי מצב הבדיקה", "error"),
  });

  const deleteMutation = useMutation({
    mutationFn: scheduledChecksApi.remove,
    onSuccess: () => {
      addNotification("בדיקה מתוזמנת נמחקה", "info");
      invalidate();
    },
    onError: (err) => addNotification(err?.response?.data?.detail || "שגיאה במחיקת הבדיקה", "error"),
  });

  const runNowMutation = useMutation({
    mutationFn: (id) => { setRunningId(id); return scheduledChecksApi.runNow(id); },
    // runningId is a single shared slot, same as firedId below (which already carries the fix
    // and its own explaining comment) -- running a second check while the first's request is
    // still in flight otherwise let either settling clear the OTHER check's "running" spinner
    // early, re-enabling its button before that request actually finished.
    onSettled: (_data, _error, id) => setRunningId((current) => (current === id ? null : current)),
    onSuccess: (result, id) => {
      invalidate();
      setFiredId(id);
      // Firing check A then check B within 3s used to have A's timer clear B's still-fresh
      // checkmark early -- only clear if this id is still the one showing.
      setTimeout(() => setFiredId((cur) => (cur === id ? null : cur)), 3000);
      if (result?.run_id) navigate(`/agent-runs/${result.run_id}`);
    },
    onError: (err) => addNotification(err?.response?.data?.detail || "שגיאה בהפעלת הבדיקה", "error"),
  });

  return (
    <div className="bg-white rounded-xl border shadow-sm p-5 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-gray-700">בדיקות מתוזמנות ({checks.length})</h3>
        {canManage && (
          <button
            type="button"
            onClick={() => setShowForm((v) => !v)}
            className="text-xs text-brand-600 hover:underline"
          >
            {showForm ? "ביטול" : "+ הוסף בדיקה"}
          </button>
        )}
      </div>

      {canManage && showForm && (
        <form
          onSubmit={(e) => { e.preventDefault(); createMutation.mutate(form); }}
          className="rounded-lg border bg-gray-50 p-3 space-y-2"
        >
          <input
            required
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="שם הבדיקה *"
            className="w-full border rounded px-2 py-1.5 text-sm"
          />
          <input
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            placeholder="תיאור (אופציונלי)"
            className="w-full border rounded px-2 py-1.5 text-sm"
          />
          <select
            value={form.interval_hours}
            onChange={(e) => setForm((f) => ({ ...f, interval_hours: Number(e.target.value) }))}
            className="w-full border rounded px-2 py-1.5 text-sm"
          >
            {INTERVAL_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          {createMutation.error && (
            <p className="text-xs text-red-600">{createMutation.error?.response?.data?.detail || "שגיאה"}</p>
          )}
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={createMutation.isPending}
              className="bg-brand-600 hover:bg-brand-700 text-white px-3 py-1.5 rounded text-xs font-medium disabled:opacity-50"
            >
              {createMutation.isPending ? "שומר..." : "צור בדיקה"}
            </button>
          </div>
        </form>
      )}

      {isLoading ? (
        <div className="text-center py-4">
          <div className="animate-spin inline-block rounded-full h-5 w-5 border-b-2 border-brand-600" />
        </div>
      ) : isError ? (
        // The scheduled-check API is admin/owner-only, while `canManage` also follows a per-user agents_manage grant:
        // a refused (403) or failed load used to read as "no scheduled checks" and every button then failed.
        <p role="alert" className="text-sm text-red-600 py-2">
          {loadError?.response?.status === 403
            ? "אין הרשאה לצפות בבדיקות המתוזמנות (למנהלים בלבד)"
            : "לא ניתן לטעון את הבדיקות המתוזמנות"}
        </p>
      ) : checks.length === 0 ? (
        <div>
          <p className="text-sm text-gray-400 py-2">אין בדיקות מתוזמנות לסוכן זה</p>
          {canManage && (
            <p className="text-xs text-gray-400">אפשר להוסיף בדיקה חדשה מהכפתור שלמעלה.</p>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {checks.map((check) => (
            <div key={check.id} className="flex items-center justify-between rounded-lg border p-3 bg-gray-50 gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span
                    role="status"
                    aria-label={check.is_enabled ? "פעיל" : "מושבת"}
                    title={check.is_enabled ? "פעיל" : "מושבת"}
                    className={`w-2 h-2 rounded-full shrink-0 ${check.is_enabled ? "bg-green-500" : "bg-gray-300"}`}
                  />
                  <p className="text-sm font-medium text-gray-800 truncate">{check.name}</p>
                  {check.latest_run_status && (
                    check.latest_run_id
                      ? <Link to={`/agent-runs/${check.latest_run_id}`}><StatusChip status={check.latest_run_status} /></Link>
                      : <StatusChip status={check.latest_run_status} />
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-0.5 mr-4">
                  {INTERVAL_OPTIONS.find((o) => o.value === check.interval_hours)?.label || `${check.interval_hours}ש`}
                  {check.next_run_at && ` · הבא: ${fmtDate(check.next_run_at)}`}
                  {check.last_run_at && ` · אחרון: ${fmtDate(check.last_run_at)}`}
                </p>
              </div>
              {canManage && (
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    type="button"
                    onClick={() => runNowMutation.mutate(check.id)}
                    disabled={runningId === check.id}
                    title="הפעל עכשיו"
                    className={`text-xs px-2 py-1 rounded border disabled:opacity-50 ${
                      firedId === check.id
                        ? "bg-green-50 text-green-700 border-green-300"
                        : "hover:bg-violet-50 text-violet-600 border-violet-200"
                    }`}
                  >
                    {runningId === check.id ? "…" : firedId === check.id ? "✓" : "▶"}
                  </button>
                  <button
                    type="button"
                    onClick={() => toggleMutation.mutate({ id: check.id, is_enabled: !check.is_enabled })}
                    disabled={toggleMutation.isPending && toggleMutation.variables?.id === check.id}
                    className={`text-xs px-2 py-1 rounded border ${check.is_enabled ? "hover:bg-amber-50 text-amber-600 border-amber-200" : "hover:bg-green-50 text-green-600 border-green-200"} disabled:opacity-50`}
                  >
                    {check.is_enabled ? "השהה" : "הפעל"}
                  </button>
                  <button
                    type="button"
                    onClick={() => { if (window.confirm(`למחוק את הבדיקה "${check.name}"?`)) deleteMutation.mutate(check.id); }}
                    disabled={deleteMutation.isPending && deleteMutation.variables === check.id}
                    className="text-xs px-2 py-1 rounded border hover:bg-red-50 text-red-500 border-red-200 disabled:opacity-50"
                  >
                    מחק
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── AgentDetailPage ───────────────────────────────────────────────────────────

export default function AgentDetailPage() {
  const { id } = useParams();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [showEdit, setShowEdit] = useState(false);
  const { can: canAgent } = useAgentPermissions();
  const canManage = canAgent("agents_manage");
  const canRun = canAgent("agents_run");
  // The scheduled-check API is admin/owner-only, while agents_manage is also a per-user grant: offer "add check" only to who the API accepts.
  const role = useAuth()?.user?.role;
  const canManageChecks = canManage && (role === "admin" || role === "owner");
  const { addNotification } = useNotification();

  const {
    data: agent,
    isLoading,
    isError: agentFailed,
    error: agentError,
    refetch: refetchAgent,
  } = useQuery({
    queryKey: ["agent", id],
    queryFn: () => agentsApi.get(id),
    enabled: !!id,
  });

  const { data: runs = [], isLoading: runsLoading, isError: runsFailed } = useQuery({
    queryKey: ["agentRuns", "agent", id],
    queryFn: () => agentRunsApi.listByAgent(id, { limit: RECENT_RUNS_LIMIT }),
    enabled: !!id,
    refetchInterval: 15000,
  });

  // The header states the same effective presence the workspace badge derives (busy while a real
  // run is in progress, offline when disabled/away). It only OBSERVES the workspace query the
  // workspace below already issues (enabled: false never fetches), so the page keeps one request
  // and the header cannot fall back to the stored `availability_status` once that payload lands.
  const { data: presenceWorkspace } = useQuery({
    queryKey: ["agent-workspace", id],
    queryFn: () => agentsApi.workspace(id),
    enabled: false,
  });

  const updateMutation = useMutation({
    mutationFn: (data) => agentsApi.update(id, data),
    onSuccess: () => {
      setShowEdit(false);
      qc.invalidateQueries({ queryKey: ["agent", id] });
      qc.invalidateQueries({ queryKey: ["agents"] });
      // agents-dash/agents-layout-badge/agents-dash-filter/cc-agents all poll agentsApi.list()
      // under their own keys with no shared prefix with ["agents"] -- a rename here would otherwise
      // show the old name/count until their own refetchInterval (or, for agents-dash-filter, which
      // has none, until its staleTime) fires.
      qc.invalidateQueries({ queryKey: ["agents-dash"] });
      qc.invalidateQueries({ queryKey: ["agents-layout-badge"] });
      qc.invalidateQueries({ queryKey: ["agents-dash-filter"] });
      qc.invalidateQueries({ queryKey: ["cc-agents"] });
    },
    onError: (err) => addNotification(err?.response?.data?.detail || "שגיאה בעדכון הסוכן", "error"),
  });

  const checkMutation = useMutation({
    mutationFn: () => agentRunsApi.systemCheck({
      agent_id: id,
      check_name: `בדיקה של ${agent?.name || "סוכן"}`,
      description: `בדיקה ידנית שהופעלה על-ידי מפעיל מדף פרטי הסוכן`,
    }),
    onSuccess: (run) => navigate(`/agent-runs/${run.id}`),
    onError: (err) => addNotification(err?.response?.data?.detail || "שגיאה בהפעלת בדיקת מערכת", "error"),
  });

  // The conversation lives in the workspace (EmployeeWorkspace) — the header action jumps there and
  // puts the cursor straight into the composer, instead of mounting a second conversation surface.
  const goToEmployeeConversation = () => {
    const el = document.getElementById("employee-conversation");
    el?.scrollIntoView?.({ behavior: "smooth", block: "start" });
    el?.querySelector("textarea")?.focus?.();
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
      </div>
    );
  }

  // Only when there is nothing to show: a failed background refetch (focus / after a save) used to swap the whole page for this
  // message, unmounting an open edit dialog together with the technician's unsaved changes.
  //
  // The load outcome is stated as what it actually was (house pattern — see the scheduled-checks
  // section below): this route carries no role gate of its own, so a user without agents_view can
  // reach it and the API is what refuses. The single sentence this replaces claimed a refused
  // read was "maybe deleted, or a temporary fault", which is false for the one permission case it
  // will actually be hit by — and the outage arm had no way to retry.
  if (!agent) {
    const loadStatus = agentError?.response?.status;
    if (loadStatus === 403) {
      return (
        <div role="alert" className="text-center py-16">
          <p className="text-red-600 text-sm">אין הרשאה לצפות בעובד זה</p>
          <p className="text-xs text-gray-500 mt-1">הרשאת הצפייה בעובדים הדיגיטליים (agents_view) חסרה לחשבון שלך — פנו למנהל המערכת.</p>
          <Link to="/agents" className="text-xs text-brand-600 hover:underline mt-2 block">← חזרה לסוכנים</Link>
        </div>
      );
    }
    if (loadStatus === 404) {
      return (
        <div className="text-center py-16">
          <p className="text-red-600 text-sm">העובד לא נמצא</p>
          <p className="text-xs text-gray-500 mt-1">ייתכן שהעובד נמחק מאז הפעם האחרונה שנפתח.</p>
          <Link to="/agents" className="text-xs text-brand-600 hover:underline mt-2 block">← חזרה לסוכנים</Link>
        </div>
      );
    }
    return (
      <div className="text-center py-16">
        <p className="text-red-600 text-sm">לא ניתן לטעון את הסוכן</p>
        <p className="text-xs text-gray-500 mt-1">ייתכן שיש תקלה זמנית — אפשר לנסות שוב.</p>
        <button
          type="button"
          onClick={() => refetchAgent()}
          className="text-xs text-brand-600 hover:underline mt-2"
        >
          נסה שוב
        </button>
        <Link to="/agents" className="text-xs text-brand-600 hover:underline mt-2 block">← חזרה לסוכנים</Link>
      </div>
    );
  }

  // Effective presence from the workspace payload; the stored self-status is only the fallback
  // while that payload is still loading or absent.
  const headerStatus = presenceWorkspace?.agent?.effective_status ?? agent.availability_status;
  const headerDrawnStatus = drawnStatus(headerStatus, agent.is_enabled);

  return (
    <div className="space-y-5 max-w-5xl mx-auto" dir="rtl">
      {/* A failed BACKGROUND refetch keeps the loaded page (the rule above) — but silently. The
          surfaces below then show data that may no longer be current; say so, without unmounting
          anything, and offer the retry the failure previously had no way to get. */}
      {agentFailed && (
        <div role="alert" className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2 flex flex-wrap items-center gap-2">
          רענון נתוני העובד נכשל — ייתכן שהנתונים המוצגים אינם מעודכנים.
          <button type="button" onClick={() => refetchAgent()} className="underline">נסה שוב</button>
        </div>
      )}
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-xs text-gray-400 mb-1">
            <Link to="/agents" className="hover:text-gray-600">סוכנים</Link>
            <span>/</span>
            <span className="text-gray-600">{agent.name}</span>
          </div>
          <div className="flex items-center gap-3">
            <EmployeeAvatar
              name={agent.name}
              avatarKey={agent.avatar_key}
              avatarColor={agent.avatar_color}
              status={headerStatus}
              isEnabled={agent.is_enabled}
              size="lg"
              showStatus
            />
            <div>
              <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                {agent.name}
                <span className={`text-sm font-normal px-2 py-0.5 rounded-full ${
                  agent.is_enabled
                    ? "bg-green-100 text-green-700"
                    : "bg-gray-100 text-gray-500"
                }`}>
                  {agent.is_enabled ? "פעיל" : "מושבת"}
                </span>
                {STATUS_META[headerDrawnStatus] && (
                  <span className="text-xs font-normal text-gray-500">
                    · {STATUS_META[headerDrawnStatus].label}
                  </span>
                )}
              </h1>
              {agent.role_title && (
                <p className="text-sm font-medium text-violet-700">{agent.role_title}</p>
              )}
              {agent.description && (
                <p className="text-sm text-gray-500 mt-0.5">{agent.description}</p>
              )}
            </div>
          </div>
          {Array.isArray(agent.specialties) && agent.specialties.length > 0 && (
            <div className="mt-2">
              <div className="flex flex-wrap gap-1">
                {agent.specialties.map((s) => (
                  <span key={s} className="text-[11px] px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 border border-violet-200">{s}</span>
                ))}
              </div>
              {/* Declared intent, not assignment: these tags are what the builder/editor declared
                  for the employee (Agent.specialties) — no Skill record, tool or connection is
                  created by declaring one, and a skill requested from the wizard exists only once
                  the server published a record for it. The server-confirmed records live in the
                  workspace's skill-assignment section below. */}
              <p className="text-[11px] text-gray-400 mt-1">
                תחומי התמחות מוצהרים — הצהרת פריסה, לא שיוך סקיל מאושר; הסקילים ששויכו בפועל מוצגים ב"מצב שיוך סקילים" שבסביבת העבודה.
              </p>
            </div>
          )}
          {agent.intro_text && (
            <p className="text-sm text-gray-600 italic mt-2 border-r-2 border-violet-200 pr-2">“{agent.intro_text}”</p>
          )}
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <Link
            to={`/agents/${id}/cv`}
            className="flex items-center gap-1.5 text-sm font-medium px-3 py-2 rounded-lg border border-violet-200 text-violet-700 hover:bg-violet-50 transition-colors"
          >
            📄 פרופיל מקצועי · CV
          </Link>
          <Link
            to={`/agents/${id}/operations`}
            className="flex items-center gap-1.5 text-sm font-medium px-3 py-2 rounded-lg border border-amber-200 text-amber-700 hover:bg-amber-50 transition-colors"
          >
            🛠️ תפעול העובד
          </Link>
          <Link
            to={`/agents/${id}/skills-learning`}
            className="flex items-center gap-1.5 text-sm font-medium px-3 py-2 rounded-lg border border-teal-200 text-teal-700 hover:bg-teal-50 transition-colors"
          >
            🎓 מיומנויות ולמידה
          </Link>
          {(role === "admin" || role === "owner") && (
            // FULL-0128 / PROD-OPS-042: the EVALUATE/IMPROVE surface. The workspace itself is
            // read-only and its routes are Admin/Owner-gated, so the entry follows the same role.
            <Link
              to="/agent-evaluations"
              className="flex items-center gap-1.5 text-sm font-medium px-3 py-2 rounded-lg border border-sky-200 text-sky-700 hover:bg-sky-50 transition-colors"
            >
              🧪 סביבת הערכה
            </Link>
          )}
          {agent.pending_runs_count > 0 && (
            <Link
              to="/agent-runs"
              className="flex items-center gap-1.5 bg-orange-50 border border-orange-200 text-orange-700 text-sm font-medium px-3 py-2 rounded-lg hover:bg-orange-100 transition-colors"
            >
              <span className="text-base">⏳</span>
              {agent.pending_runs_count} ממתינות לאישור
            </Link>
          )}
          {canRun && agent.is_enabled && (
            <button
              type="button"
              onClick={goToEmployeeConversation}
              className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-3 py-2 rounded-lg transition-colors"
            >
              💬 שיחה עם {agent.name}
            </button>
          )}
          {canManage && agent.is_enabled && (
            <button
              type="button"
              onClick={() => checkMutation.mutate()}
              disabled={checkMutation.isPending}
              className="flex items-center gap-1.5 bg-violet-600 hover:bg-violet-700 text-white text-sm font-medium px-3 py-2 rounded-lg transition-colors disabled:opacity-50"
            >
              {checkMutation.isPending ? "מפעיל..." : "▶ הפעל בדיקה"}
            </button>
          )}
        </div>
      </div>

      <CockpitSection
        title="מרכז העבודה"
        description="מה העובד עושה כעת — עבודה נוכחית, קריאות בטיפול, שיחות אחרונות ויומן פעילות"
      >
        {/* HELPDESK-CONVERSATIONAL-WORKER-001 / DIGEMP-EMPLOYEE-CONVERSATION-WORKSPACE-FRONTEND-020:
            the ticket-less conversation with THIS specific agent lives inside the workspace's own
            conversation section (#employee-conversation), so there is a single chat surface. The
            growth profile, experience timeline, skill assignment state, skill history, learning
            history and the specialist / remote readiness readouts all reuse data this page already
            loaded: the agent detail payload (growth_profile / the Skill assignment-state projection /
            skill_history / learning_history, additive), the workspace projection and
            the audited run records (experience outcomes) — no second fetch, and the same agents_view
            gate that loaded this page already governs what the operator may see here. customer_id is
            passed through for a customer-scoped agent; a global agent (client_id null) still creates a
            conversation fine without one — conversation_service only rejects an EXPLICIT mismatch. */}
        <EmployeeWorkspace
          agentId={id}
          agentDetail={agent}
          customerId={agent.client_id || undefined}
          runs={runs}
          runsLoading={runsLoading}
          runsError={runsFailed}
        />
      </CockpitSection>

      <CockpitSection
        title="ריצות וביצועים"
        description="מדדי פעילות, היסטוריית ריצות, בדיקות אוטומטיות והערכת ביצועים"
      >
        <StatsRow runs={runs} />
        <RecentRunsSection runs={runs} isLoading={runsLoading} isError={runsFailed} agentId={id} />
        <ScheduledChecksSection agentId={id} agentName={agent.name} canManage={canManageChecks} />
        <SkillsSection agentId={id} />
      </CockpitSection>

      <CockpitSection
        title="חיבורים ואינטגרציות"
        description="מערכות מחוברות, בדיקות חיבור ויכולות שנלמדו — לקריאה ולתפעול מפוקח"
      >
        <ConnectionsSection agentId={id} connections={agent.connections} canManage={canManage} agentClientId={agent.client_id} />
        {(() => {
          const esetConnection = (agent.connections || []).find((connection) => connection.system_type === "eset_protect");
          if (!esetConnection) return null;
          return (
            <div className="bg-white rounded-xl border shadow-sm p-5">
              <ESETAdvisoryPanel agentId={id} connectionId={esetConnection.id} />
            </div>
          );
        })()}
      </CockpitSection>

      <CockpitSection
        title="הגדרות והרשאות"
        description="זהות העובד, מודל, רמת הרשאה, כלים מותרים ורמת אוטונומיה"
      >
        <ConfigSection agent={agent} onEdit={() => setShowEdit(true)} canManage={canManage} />
      </CockpitSection>

      {showEdit && (
        <EditModal
          agent={agent}
          saving={updateMutation.isPending}
          error={updateMutation.error}
          onSave={(data) => updateMutation.mutate(data)}
          onCancel={() => setShowEdit(false)}
        />
      )}
    </div>
  );
}
